const CMRP_PAGE_SCROLL_LOCK_CLASS = 'cmrp-scroll-lock';

function cmrpGetPageScrollLockState() {
  if (typeof window === 'undefined') return null;
  if (!window.__cmrpPageScrollLock) {
    window.__cmrpPageScrollLock = { count: 0, scrollY: 0 };
  }
  return window.__cmrpPageScrollLock;
}

function cmrpAcquirePageScrollLock() {
  if (typeof document === 'undefined') return;
  const state = cmrpGetPageScrollLockState();
  if (!state) return;

  state.count += 1;
  if (state.count !== 1) return;

  state.scrollY =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;

  document.documentElement.classList.add(CMRP_PAGE_SCROLL_LOCK_CLASS);
  document.body.classList.add(CMRP_PAGE_SCROLL_LOCK_CLASS);
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${state.scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function cmrpReleasePageScrollLock() {
  if (typeof document === 'undefined') return;
  const state = cmrpGetPageScrollLockState();
  if (!state) return;

  state.count = Math.max(0, state.count - 1);
  if (state.count !== 0) return;

  document.documentElement.classList.remove(CMRP_PAGE_SCROLL_LOCK_CLASS);
  document.body.classList.remove(CMRP_PAGE_SCROLL_LOCK_CLASS);
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';

  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) {
    active.blur();
  }

  const y = state.scrollY || 0;
  state.scrollY = 0;
  cmrpInstantScrollTo(y);
}

/** @type {{ keydown?: (e: KeyboardEvent) => void, focusin?: (e: FocusEvent) => void, previous?: HTMLElement | null, container?: HTMLElement | null }} */
let __cmrpChoiceFocusTrap = {};

function cmrpChoiceGetFocusable(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
    )
  ).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });
}

/**
 * Move focus into a choice drawer sheet and trap Tab until released.
 * @param {HTMLElement} container
 * @param {HTMLElement | null} [trigger]
 */
function cmrpChoiceTrapFocus(container, trigger = null) {
  cmrpChoiceReleaseFocusTrap(false);
  if (!(container instanceof HTMLElement)) return;

  __cmrpChoiceFocusTrap.previous =
    trigger instanceof HTMLElement
      ? trigger
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  __cmrpChoiceFocusTrap.container = container;

  const sheet =
    container.querySelector('.choice-drawer-sheet') || container;
  if (!sheet.hasAttribute('tabindex')) {
    sheet.setAttribute('tabindex', '-1');
  }

  const focusInitial = () => {
    const focusable = cmrpChoiceGetFocusable(sheet);
    const preferred =
      sheet.querySelector('.choice-drawer-close') ||
      focusable[0] ||
      sheet;
    preferred?.focus?.({ preventScroll: true });
  };
  requestAnimationFrame(focusInitial);

  __cmrpChoiceFocusTrap.keydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      const closeBtn = sheet.querySelector('.choice-drawer-close');
      if (closeBtn instanceof HTMLElement) {
        closeBtn.click();
      }
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = cmrpChoiceGetFocusable(sheet);
    if (!focusable.length) {
      event.preventDefault();
      sheet.focus({ preventScroll: true });
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === sheet)) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  __cmrpChoiceFocusTrap.focusin = (event) => {
    if (event.target instanceof Node && !sheet.contains(event.target)) {
      const focusable = cmrpChoiceGetFocusable(sheet);
      (focusable[0] || sheet).focus({ preventScroll: true });
    }
  };

  document.addEventListener('keydown', __cmrpChoiceFocusTrap.keydown, true);
  document.addEventListener('focusin', __cmrpChoiceFocusTrap.focusin, true);
}

/**
 * @param {boolean} [restoreFocus=true]
 */
function cmrpChoiceReleaseFocusTrap(restoreFocus = true) {
  if (__cmrpChoiceFocusTrap.keydown) {
    document.removeEventListener('keydown', __cmrpChoiceFocusTrap.keydown, true);
  }
  if (__cmrpChoiceFocusTrap.focusin) {
    document.removeEventListener('focusin', __cmrpChoiceFocusTrap.focusin, true);
  }
  const previous = __cmrpChoiceFocusTrap.previous;
  __cmrpChoiceFocusTrap = {};
  if (!restoreFocus) return;
  requestAnimationFrame(() => {
    if (previous instanceof HTMLElement && previous.isConnected) {
      try {
        previous.focus({ preventScroll: true });
      } catch (_) {
        // ignore
      }
    }
  });
}

function cmrpInstantScrollTo(y) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  html.scrollTop = y;
  if (document.body) document.body.scrollTop = y;
  window.scrollTo(0, y);
  html.style.scrollBehavior = prev;
}

/**
 * Keep `el`’s viewport Y unchanged across a layout mutate so extra height
 * pushes following content down instead of shifting this node up.
 * @param {Element|null|undefined} el
 * @param {() => void} mutate
 */
function cmrpKeepViewportTop(el, mutate) {
  if (typeof mutate !== 'function') return;
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    mutate();
    return;
  }
  const top = el.getBoundingClientRect().top;
  mutate();
  const delta = el.getBoundingClientRect().top - top;
  if (Math.abs(delta) < 1) return;
  cmrpInstantScrollTo(
    (window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0) + delta
  );
}

function cmrpUsedPrintVariantNoticeText() {
  if (typeof window !== 'undefined' && window.CMRP_USED_PRINT_VARIANT_NOTICE) {
    return window.CMRP_USED_PRINT_VARIANT_NOTICE;
  }
  return 'Used materials: might not include supplemental materials like access cards or CDs.';
}

function cmrpEscapeVariantNoticeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/** Parent product id for choice card wiring (unified view uses print parent id). */
function cmrpResolveDisplayProductId(product, variant) {
  if (window.ProductSystem?.resolveDisplayProductId) {
    return window.ProductSystem.resolveDisplayProductId(product, variant);
  }
  const raw = product?.id ?? variant?.parentProductId;
  if (raw == null || raw === '') return '';
  return String(raw).split('/').pop();
}

/** Choice row id from the rendered card (plus button / row checkbox / details panel). */
function cmrpResolveChoiceCardProductId(materialItem) {
  if (!materialItem) return null;
  const plus = materialItem.querySelector(
    '.choice-product-card__plus-select[data-product-id]'
  );
  if (plus) return plus.getAttribute('data-product-id');
  const outer =
    materialItem.closest?.('.choice-item') || materialItem.__cmrpChoiceOuter;
  const outerPlus = outer?.querySelector?.(
    '.choice-product-card__plus-select[data-product-id]'
  );
  if (outerPlus) return outerPlus.getAttribute('data-product-id');
  const panel = materialItem.closest?.('[id^="choice-details-product-"]') ||
    (materialItem.id && String(materialItem.id).startsWith('choice-details-product-')
      ? materialItem
      : null);
  if (panel?.id) {
    return panel.id.replace(/^choice-details-product-/, '');
  }
  const rowCb = materialItem.querySelector(
    '.purchase-choice-checkbox[id^="check-box-product-"]'
  );
  if (rowCb?.id) {
    const m = rowCb.id.match(/^check-box-product-(.+)$/);
    if (m) return m[1];
  }
  return null;
}

/** Short in-card label for digital delivery fee (e.g. "+ $5.99 digital delivery fee"). */
function cmrpDigitalDeliveryFeeLabel() {
  const raw =
    (typeof document !== 'undefined' &&
      document.querySelector('.digital-fee-amount')?.textContent?.trim()) ||
    (typeof window !== 'undefined' &&
      (window.digital_product_config?.variant_price ||
        window.digital_product_config?.price)) ||
    '5.99';
  const amount = String(raw).replace(/^\$/, '').trim() || '5.99';
  return `+ $${amount} digital delivery fee`;
}

const CHOICE_TOAST_DURATION_MS = 6000;
const CHOICE_TOAST_CHECK_SVG =
  '<svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.23695 2.1967C9.91967 1.81651 11.6802 1.99045 13.256 2.69258C13.7605 2.91736 14.3517 2.69062 14.5764 2.18615C14.8012 1.68168 14.5745 1.0905 14.07 0.865724C12.1003 -0.0119392 9.89959 -0.229366 7.79619 0.24587C5.69278 0.721106 3.79935 1.86354 2.39828 3.5028C0.997207 5.14205 0.163562 7.19029 0.0216768 9.34204C-0.120209 11.4938 0.437266 13.6338 1.61096 15.4428C2.78465 17.2518 4.51168 18.633 6.53447 19.3803C8.55726 20.1276 10.7674 20.201 12.8354 19.5896C14.9033 18.9782 16.7182 17.7147 18.0093 15.9875C19.3005 14.2604 19.9988 12.1616 20 10.0051V9.17715C20 8.62486 19.5523 8.17715 19 8.17715C18.4477 8.17715 18 8.62486 18 9.17715V10.0046C17.999 11.7297 17.4404 13.4083 16.4075 14.79C15.3745 16.1718 13.9226 17.1826 12.2683 17.6717C10.6139 18.1608 8.84581 18.1021 7.22758 17.5042C5.60935 16.9064 4.22772 15.8015 3.28877 14.3542C2.34981 12.907 1.90383 11.195 2.01734 9.47363C2.13085 7.75223 2.79777 6.11364 3.91862 4.80224C5.03948 3.49084 6.55423 2.57689 8.23695 2.1967Z" fill="white"/><path d="M19.687 3.72665C20.0883 3.34722 20.1061 2.71431 19.7267 2.31299C19.3472 1.91168 18.7143 1.89393 18.313 2.27336L10.5381 9.62414L8.68665 7.87541C8.28515 7.49618 7.65224 7.51425 7.27301 7.91575C6.89379 8.31726 6.91185 8.95016 7.31335 9.32939L9.85182 11.727C10.2374 12.0911 10.8401 12.091 11.2255 11.7266L19.687 3.72665Z" fill="white"/></svg>';
const CHOICE_TOAST_INFO_SVG =
  '<svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 9C10.5523 9 11 9.44771 11 10V14C11 14.5523 10.5523 15 10 15C9.44771 15 9 14.5523 9 14V10C9 9.44771 9.44771 9 10 9Z" fill="white"/><path d="M9.95238 5C9.4264 5 9 5.44772 9 6C9 6.55228 9.4264 7 9.95238 7H10.0476C10.5736 7 11 6.55228 11 6C11 5.44772 10.5736 5 10.0476 5H9.95238Z" fill="white"/><path fill-rule="evenodd" clip-rule="evenodd" d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10ZM10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2Z" fill="white"/></svg>';
const CHOICE_TOAST_CLOSE_SVG =
  '<svg width="18" height="18" viewBox="0 0 10.5 10.5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.21967 0.21967C0.512563 -0.0732233 0.987437 -0.0732233 1.28033 0.21967L5.25 4.18934L9.21967 0.21967C9.51256 -0.0732233 9.98744 -0.0732233 10.2803 0.21967C10.5732 0.512563 10.5732 0.987437 10.2803 1.28033L6.31066 5.25L10.2803 9.21967C10.5732 9.51256 10.5732 9.98744 10.2803 10.2803C9.98744 10.5732 9.51256 10.5732 9.21967 10.2803L5.25 6.31066L1.28033 10.2803C0.987437 10.5732 0.512563 10.5732 0.21967 10.2803C-0.0732233 9.98744 -0.0732233 9.51256 0.21967 9.21967L4.18934 5.25L0.21967 1.28033C-0.0732233 0.987437 -0.0732233 0.512563 0.21967 0.21967Z" fill="currentColor"/></svg>';

/**
 * When a used-print `.variant-notice` is shown, hide the generic digital
 * delivery line in the same collapse. Selecting a digital-fee variant does not
 * suppress this notice (the in-card fee strip is additive).
 * @param {Element} collapseContent
 * @param {boolean} suppressed
 */
function cmrpSetDigitalDeliveryNoticeSuppressed(collapseContent, suppressed) {
  collapseContent.querySelectorAll('.digital-delivery-notice').forEach(el => {
    if (suppressed) {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
    }
  });
}

/**
 * @param {Element} row
 * @returns {HTMLElement}
 */
function cmrpEnsureDigitalFeeStrip(row) {
  let el = row.querySelector('.purchase-type-digital-fee');
  if (!el) {
    el = document.createElement('div');
    el.className = 'purchase-type-digital-fee';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="purchase-type-digital-fee__inner"><span class="purchase-type-digital-fee__text"></span></div>';
    row.appendChild(el);
  } else if (el.parentElement === row && row.lastElementChild !== el) {
    row.appendChild(el);
  }
  const text = el.querySelector('.purchase-type-digital-fee__text');
  if (text) text.textContent = cmrpDigitalDeliveryFeeLabel();
  return el;
}

function cmrpAnnounceFeaturedFee(el, label) {
  let live = el.querySelector('.purchase-type-digital-fee__announce');
  if (!live) {
    live = document.createElement('span');
    live.className = 'visually-hidden purchase-type-digital-fee__announce';
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    el.appendChild(live);
  }
  live.textContent = '';
  requestAnimationFrame(() => {
    if (live.isConnected) live.textContent = label;
  });
  window.setTimeout(() => {
    if (live.isConnected) live.remove();
  }, 1500);
}

function cmrpFeeDescribedBy(input, id, linked) {
  if (!input || !id) return;
  const ids = (input.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter(item => item && item !== id);
  if (linked) ids.push(id);
  if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
  else input.removeAttribute('aria-describedby');
}

/**
 * Keep aria in sync. Visual expand/collapse is CSS-only
 * via `.purchase-type.selected` — no extra is-expanded class.
 * The fee is read with the purchase option when the strip is featured,
 * and announced at that moment.
 * @param {Element} row
 * @param {boolean} expanded
 */
function cmrpSetDigitalFeeStripExpanded(row, expanded) {
  const el = row.querySelector('.purchase-type-digital-fee');
  if (!el) return;
  const text = el.querySelector('.purchase-type-digital-fee__text');
  const input = row.querySelector(
    'input.purchase-checkbox, input.purchase-choice-checkbox'
  );
  const label = text?.textContent?.trim() || cmrpDigitalDeliveryFeeLabel();
  if (text && text.textContent.trim() !== label) text.textContent = label;
  if (text && !text.id) {
    text.id = `digital-fee-label-${Math.random().toString(36).slice(2, 10)}`;
  }
  const wasFeatured = el.getAttribute('data-fee-featured') === 'true';
  if (text) text.removeAttribute('aria-hidden');

  el.removeAttribute('tabindex');
  el.removeAttribute('role');
  el.removeAttribute('aria-label');
  el.removeAttribute('aria-labelledby');

  if (expanded) {
    el.removeAttribute('aria-hidden');
    el.setAttribute('data-fee-featured', 'true');
    if (text) {
      text.setAttribute('tabindex', '0');
      text.setAttribute('role', 'note');
    }
    if (text && input) cmrpFeeDescribedBy(input, text.id, true);
    if (text && !wasFeatured) cmrpAnnounceFeaturedFee(el, label);
  } else {
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('data-fee-featured');
    if (text) {
      text.removeAttribute('tabindex');
      text.removeAttribute('role');
    }
    el.querySelector('.purchase-type-digital-fee__announce')?.remove();
    if (text && input) cmrpFeeDescribedBy(input, text.id, false);
  }
}

/**
 * Keep `.selected` in sync with the checkbox so CSS can avoid
 * `.purchase-type:has(.purchase-checkbox:checked)` during scroll.
 * @param {ParentNode | null | undefined} root
 */
function cmrpSyncPurchaseTypeSelectedClass(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  root.querySelectorAll('.purchase-type').forEach(row => {
    const checked = !!row.querySelector(
      '.purchase-checkbox:checked, .purchase-choice-checkbox:checked'
    );
    row.classList.toggle('selected', checked);
  });
}

window.cmrpSyncPurchaseTypeSelectedClass = cmrpSyncPurchaseTypeSelectedClass;

if (typeof document !== 'undefined') {
  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (
      !target.classList.contains('purchase-checkbox') &&
      !target.classList.contains('purchase-choice-checkbox')
    ) {
      return;
    }
    const row = target.closest('.purchase-type');
    if (!row) return;
    const checked = !!row.querySelector(
      '.purchase-checkbox:checked, .purchase-choice-checkbox:checked'
    );
    row.classList.toggle('selected', checked);
  });
}

/**
 * Place used-print `.variant-notice` immediately above the variants grid
 * (same slot as `.digital-delivery-notice`).
 * @param {Element} collapseContent
 * @param {Element} grid
 * @param {HTMLElement} slot
 */
function cmrpPlaceVariantNoticeAboveGrid(collapseContent, grid, slot) {
  if (grid.parentNode === collapseContent) {
    collapseContent.insertBefore(slot, grid);
  } else if (grid.parentNode) {
    grid.parentNode.insertBefore(slot, grid);
  } else {
    collapseContent.prepend(slot);
  }
}

/**
 * CMRP: sync variant notices in a collapse.
 * - Used-print: plain-text `.variant-notice` above the purchase options whenever
 *   any used-print row exists (not tied to selection).
 * - Digital fee: short fee line expands inside the selected purchase-type card.
 * @param {Element | null | undefined} collapseContent
 */
function syncCmrpVariantNoticeInCollapse(collapseContent) {
  if (!collapseContent || typeof document === 'undefined') return;
  const grid =
    collapseContent.querySelector('.purchase-types') ||
    collapseContent.querySelector('.accordion-variant-options');
  if (!grid) return;

  let usedPrintMessage = '';

  grid.querySelectorAll('.purchase-type, .accordion-variant-option').forEach(row => {
    const checked = !!row.querySelector('.purchase-checkbox:checked');
    const hasDigitalFee = row.hasAttribute('data-digital-delivery-fee');

    if (hasDigitalFee) {
      cmrpEnsureDigitalFeeStrip(row);
      cmrpSetDigitalFeeStripExpanded(row, checked);
    } else {
      row.querySelector('.purchase-type-digital-fee')?.remove();
    }

    if (usedPrintMessage || hasDigitalFee) return;
    const raw = row.getAttribute('data-variant-notice');
    const msg = raw && String(raw).trim();
    if (msg) usedPrintMessage = msg;
  });

  let slot = collapseContent.querySelector(':scope > .variant-notice');
  if (!slot) {
    slot = collapseContent.querySelector('.variant-notice');
  }

  if (!usedPrintMessage) {
    if (slot) slot.remove();
  } else {
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'variant-notice';
    }
    slot.textContent = usedPrintMessage;
    cmrpPlaceVariantNoticeAboveGrid(collapseContent, grid, slot);
  }

  cmrpSetDigitalDeliveryNoticeSuppressed(
    collapseContent,
    !!usedPrintMessage
  );

  const container = collapseContent.closest('.purchase-options-container');
  if (container) {
    container.classList.toggle('has-variant-notice', !!usedPrintMessage);
    const digitalVisible = !!collapseContent.querySelector(
      '.digital-delivery-notice:not([hidden])'
    );
    container.classList.toggle('has-digital-notice', digitalVisible);
  }
}

/** Re-sync digital-fee / used-print notices across CMRP collapses and PDP panels. */
function syncAllCmrpVariantNotices() {
  if (typeof document === 'undefined') return;
  document
    .querySelectorAll('.collapse-content, .format-tab-panel-content')
    .forEach(collapse => syncCmrpVariantNoticeInCollapse(collapse));
}

window.syncCmrpVariantNoticeInCollapse = syncCmrpVariantNoticeInCollapse;
window.syncAllCmrpVariantNotices = syncAllCmrpVariantNotices;

class MaterialInteractionManager {
  constructor(config = {}) {
    this.products = config.products || [];
    this.productHandler = config.productHandler || null;
    this.updateSelectedProductsCallback =
      config.updateSelectedProductsCallback || (() => {});
    this.updateSelectionBarSummaryCallback =
      config.updateSelectionBarSummaryCallback || (() => {});

    this._choiceToastPrevCounts = {};
    this._choiceToastTimer = null;
    this._choicePageScrollLockHeld = false;

    this._bindMethods();
    this.setupCartEventListeners();
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this._handleChoiceDrawerEscape);
    }
    this._setupChoiceViewportListener();
  }

  _getSectionScope(element) {
    const materialItem = element?.closest?.('.material-item');
    return materialItem?.closest?.('.course-section') || document;
  }

  _resolveChoiceSectionId(el, fallback) {
    const portaledRoot = el?.closest?.('[id^="choice-details-product-"]');
    const scopeEl = portaledRoot?.__cmrpChoiceOuter || el;
    const root =
      scopeEl?.closest?.('.choice-products-section') ||
      el?.closest?.('.choice-products-section');
    const minEl = root?.querySelector('[id^="choice-minimum-"]');
    if (minEl?.id) return minEl.id.slice('choice-minimum-'.length);
    const fromCard = String(
      scopeEl?.closest?.('.course-card')?.dataset?.selectedSectionId ||
        el?.closest?.('.course-card')?.dataset?.selectedSectionId ||
        ''
    ).trim();
    if (fromCard) return fromCard;
    return fallback || null;
  }

  _initMultiFormatWarning(materialItem) {
    window.ProductSystem?.initMultiFormatSelectionWarning?.(materialItem);
  }

  _updateMultiFormatWarning(materialItem) {
    window.ProductSystem?.updateMultiFormatSelectionWarning?.(materialItem);
  }

  _bindMethods() {
    const methods = [
      'handleFormatSelection',
      'updatePricingForFormat',
      'togglePurchaseOption',
      'handlePurchaseSelection',
      'addMaterialEventListeners',
      'toggleViewMore',
      'toggleViewMoreChoice',
      'populateExpandedDetails',
      'getVariantByOptions',
      'findVariantAcrossProducts',
      'getSelectedFormat',
      'handleCartUpdate',
      '_handleChoiceDrawerEscape',
      '_syncChoiceDrawerScrollLock',
      '_portalChoiceDrawerOpen',
      '_portalChoiceDrawerClose',
      '_syncChoiceRowControlFromDrawer',
      '_reconcileChoiceRowCheckedFromExpanded',
      '_updateChoiceProductPlusButton',
      '_syncChoiceItemChrome',
      '_refreshChoiceSelectedSummaries',
      '_collectSelectedChoiceProducts',
      '_syncChoiceMinimumAndExtraData',
      '_hideChoiceToast',
      '_applyChoiceCollapsedVisibility',
      '_syncChoiceViewportLayout',
    ];
    methods.forEach(method => (this[method] = this[method].bind(this)));
  }

  /**
   * Close choice variant drawer on Escape (same outcome as Hide details / backdrop).
   */
  _handleChoiceDrawerEscape(e) {
    if (e.key !== 'Escape') return;
    const open = document.querySelector(
      '.material-item.choice-info.choice-show'
    );
    if (!open) return;
    const outer =
      open.__cmrpChoiceOuter ||
      open.closest('.material-item.choice-item');
    if (!outer) return;
    const viewMoreBtn = outer.querySelector('.view-more-btn-choice');
    const checkBtn = outer.querySelector('.view-check-btn-choice');
    if (viewMoreBtn?.classList.contains('expanded')) {
      this.toggleViewMoreChoice(viewMoreBtn, 'btn');
    } else if (checkBtn?.classList.contains('expanded')) {
      this.toggleViewMoreChoice(checkBtn, 'checkbox');
    }
  }

  /** Viewports where the variant block stays in flow under the card (no portal / body scroll lock). */
  _choiceDrawerUsesInlineLayout() {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(max-width: 968px)').matches;
    }
    return window.innerWidth <= 968;
  }

  _setupChoiceViewportListener() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(max-width: 968px)');
    this._choiceInlineLayout = mq.matches;
    const onChange = () => {
      const inline = mq.matches;
      if (inline === this._choiceInlineLayout) return;
      this._choiceInlineLayout = inline;
      this._syncChoiceViewportLayout();
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onChange);
    }
  }

  /**
   * Desktop collapsed summary vs mobile stacked list — keep DOM/classes in sync
   * when the viewport crosses 968px without a reload.
   */
  _syncChoiceViewportLayout() {
    if (typeof document === 'undefined') return;
    const inline = this._choiceDrawerUsesInlineLayout();

    document.querySelectorAll('.choice-products-section').forEach(section => {
      section.classList.toggle('material-item', !inline);
      const minEl = section.querySelector('[id^="choice-minimum-"]');
      const sectionID = minEl?.id
        ? minEl.id.slice('choice-minimum-'.length)
        : '';
      if (sectionID) this._syncChoiceMinimumAndExtraData(sectionID);
    });

    document
      .querySelectorAll('.material-item.choice-info.choice-show')
      .forEach((info, index) => {
        if (inline) {
          this._portalChoiceDrawerClose(info);
          return;
        }
        // Desktop drawer is exclusive; keep only the first open panel.
        if (index === 0) {
          this._portalChoiceDrawerOpen(info);
          return;
        }
        const id = this._choiceProductIdFromDetailsPanel(info);
        if (id) this._closeChoicePurchaseOptions(id);
      });
    this._syncChoiceDrawerScrollLock();
  }

  /** Lock page scroll while a choice drawer is open */
  _syncChoiceDrawerScrollLock() {
    if (typeof document === 'undefined') return;
    const open = document.querySelector(
      '.material-item.choice-info.choice-show'
    );
    const lock = !!open && !this._choiceDrawerUsesInlineLayout();
    document.body.classList.toggle('cmrp-choice-drawer-open', lock);
    if (lock && !this._choicePageScrollLockHeld) {
      cmrpAcquirePageScrollLock();
      this._choicePageScrollLockHeld = true;
    } else if (!lock && this._choicePageScrollLockHeld) {
      cmrpReleasePageScrollLock();
      this._choicePageScrollLockHeld = false;
    }
  }

  /**
   * Move choice details drawer to document.body (same idea as Preact createPortal
   * in suggested ProductsDrawer) so position:fixed is not clipped by CMRP ancestors.
   */
  _portalChoiceDrawerOpen(choiceInfo) {
    if (
      !choiceInfo ||
      typeof document === 'undefined' ||
      choiceInfo.dataset.cmrpChoiceDrawerPortaled === '1' ||
      this._choiceDrawerUsesInlineLayout()
    ) {
      return;
    }
    choiceInfo.__cmrpChoiceOuter =
      choiceInfo.closest('.material-item.choice-item') || null;
    choiceInfo.__cmrpPortalParent = choiceInfo.parentNode;
    choiceInfo.__cmrpPortalNext = choiceInfo.nextSibling;
    document.body.appendChild(choiceInfo);
    choiceInfo.dataset.cmrpChoiceDrawerPortaled = '1';
  }

  _portalChoiceDrawerClose(choiceInfo) {
    if (
      !choiceInfo ||
      choiceInfo.dataset.cmrpChoiceDrawerPortaled !== '1'
    ) {
      return;
    }
    const parent = choiceInfo.__cmrpPortalParent;
    const next = choiceInfo.__cmrpPortalNext;
    if (parent && parent.isConnected) {
      if (next && next.parentNode === parent) {
        parent.insertBefore(choiceInfo, next);
      } else {
        parent.appendChild(choiceInfo);
      }
    }
    delete choiceInfo.__cmrpPortalParent;
    delete choiceInfo.__cmrpPortalNext;
    delete choiceInfo.__cmrpChoiceOuter;
    delete choiceInfo.dataset.cmrpChoiceDrawerPortaled;
  }

  /**
   * Keep the row checkbox `expanded` class aligned with the real drawer visibility
   * so the next toggleViewMoreChoice flip matches user expectation.
   */
  _syncChoiceRowControlFromDrawer(productId) {
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productId
    );
    const cb = document.getElementById('check-box-product-' + productId);
    if (!choiceInfo || !cb) return;
    const open = choiceInfo.classList.contains('choice-show');
    cb.classList.toggle('expanded', open);
  }

  /** Match native checkbox default: checked tracks drawer-open (expanded) for choice rows */
  _reconcileChoiceRowCheckedFromExpanded(productId) {
    const cb = document.getElementById('check-box-product-' + productId);
    if (!cb) return;
    cb.checked = cb.classList.contains('expanded');
  }

  /** Product id suffix from `check-box-product-*` or `choice-info-product-*` (handles ids with dashes). */
  _choiceProductIdFromToggleButton(button) {
    if (!button?.id) return null;
    const m = button.id.match(
      /^(?:check-box-product-|choice-info-product-)(.+)$/
    );
    return m ? m[1] : null;
  }

  /**
   * Choice card + button: selected chrome, selection-order number, mobile summary.
   * aria-expanded follows the portaled drawer.
   */
  _updateChoiceProductPlusButton(productId) {
    if (typeof document === 'undefined' || !productId) return;
    const items = new Set();
    document.querySelectorAll('.choice-item').forEach(item => {
      const plus = item.querySelector('.choice-product-card__plus-select');
      const plusId = plus?.getAttribute('data-product-id');
      const pan = this._getChoiceItemPanel(item, plusId);
      const panId = pan?.id ? pan.id.replace(/^choice-details-product-/, '') : '';
      if (
        this._choiceIdsMatch(plusId, productId) ||
        this._choiceIdsMatch(panId, productId)
      ) {
        items.add(item);
        return;
      }
      const hasChecked = Array.from(
        (pan || item).querySelectorAll('.purchase-checkbox:checked')
      ).some(cb =>
        this._choiceIdsMatch(cb.getAttribute('data-product-id'), productId)
      );
      if (hasChecked) items.add(item);
    });
    items.forEach(item => this._syncChoiceItemChrome(item));

    const firstItem = items.values().next().value;
    const sectionId =
      this._resolveChoiceSectionId(firstItem) ||
      document.getElementById('check-box-product-' + productId)?.getAttribute(
        'data-section-id'
      ) ||
      firstItem
        ?.querySelector('.purchase-checkbox')
        ?.getAttribute('data-section-id');
    if (sectionId) {
      this._renumberChoiceSelections(sectionId);
      this._updateChoiceProgressHeader(sectionId);
    }
  }

  _getChoiceItemPanel(choiceItem, productId) {
    if (!choiceItem) return null;
    const nested = choiceItem.querySelector('[id^="choice-details-product-"]');
    if (nested) return nested;
    const portaled = Array.from(
      document.querySelectorAll('[id^="choice-details-product-"]')
    ).find(el => el.__cmrpChoiceOuter === choiceItem);
    if (portaled) return portaled;
    if (!productId) return null;
    const suffix = String(productId).split('/').pop();
    return (
      document.getElementById('choice-details-product-' + productId) ||
      document.getElementById('choice-details-product-' + suffix)
    );
  }

  _getCheckedChoiceCheckbox(panel, productId) {
    if (!panel) return null;
    const checked = Array.from(panel.querySelectorAll('.purchase-checkbox:checked'));
    if (!checked.length) return null;
    return (
      checked.find(cb =>
        this._choiceIdsMatch(cb.getAttribute('data-product-id'), productId)
      ) || checked[0]
    );
  }

  _syncChoiceItemChrome(choiceItem) {
    if (!choiceItem) return;
    const plus = choiceItem.querySelector('.choice-product-card__plus-select');
    const pid = plus?.getAttribute('data-product-id') || '';
    const panel = this._getChoiceItemPanel(choiceItem, pid);
    const checked = this._getCheckedChoiceCheckbox(panel, pid);
    const selected = !!checked;

    choiceItem.classList.toggle('is-choice-selected', selected);
    this._updateChoiceCardSelectionDetails(choiceItem, selected ? checked : null);
    this._syncChoiceViewOptionsButton(choiceItem);
    if (selected) {
      if (!choiceItem.dataset.choiceSelectSeq) {
        choiceItem.dataset.choiceSelectSeq = String(Date.now());
      }
      this._fillChoiceSelectedSummary(choiceItem, checked);
    } else {
      delete choiceItem.dataset.choiceSelectSeq;
      const summary = choiceItem.querySelector(
        '.choice-product-card__selected-summary'
      );
      if (summary) summary.hidden = true;
    }

    if (!plus) return;
    const icon = plus.querySelector('.choice-plus__icon');
    const countEl = plus.querySelector('.choice-plus__count');
    const open = !!panel?.classList.contains('choice-show');
    plus.setAttribute('aria-expanded', open ? 'true' : 'false');
    plus.classList.toggle('choice-product-card__plus-select--open', open);
    if (selected) {
      plus.setAttribute(
        'aria-label',
        `Selected. ${open ? 'Close' : 'Open'} purchase options.`
      );
      if (icon) icon.hidden = true;
      if (countEl) countEl.removeAttribute('hidden');
    } else {
      plus.setAttribute(
        'aria-label',
        open ? 'Close purchase options' : 'Open purchase options'
      );
      if (icon) icon.hidden = false;
      if (countEl) {
        countEl.textContent = '';
        countEl.setAttribute('hidden', '');
      }
    }
  }

  _fillChoiceSelectedSummary(choiceItem, checkedCb) {
    const summary = choiceItem?.querySelector(
      '.choice-product-card__selected-summary'
    );
    if (!summary || !checkedCb) return;

    const formatRaw = checkedCb.getAttribute('data-format') || '';
    const typeRaw = checkedCb.getAttribute('data-type') || '';
    const priceRaw = checkedCb.getAttribute('data-variant-price') || '';
    const due = checkedCb.getAttribute('data-book-rent-due-date') || '';

    const format = formatRaw
      ? formatRaw.charAt(0).toUpperCase() + formatRaw.slice(1)
      : '';
    const formatEl = summary.querySelector(
      '.choice-product-card__selected-format'
    );
    const priceEl = summary.querySelector(
      '.choice-product-card__selected-price'
    );
    const dueEl = summary.querySelector('.choice-product-card__selected-due');

    if (formatEl) {
      formatEl.textContent = [format, typeRaw].filter(Boolean).join(' | ');
    }
    if (priceEl) {
      priceEl.textContent = this._formatChoicePrice(priceRaw);
    }
    if (dueEl) {
      const row = checkedCb.closest(
        '.purchase-type, .accordion-variant-option'
      );
      const hasDigitalFee = !!(
        row?.hasAttribute('data-digital-delivery-fee') ||
        row?.querySelector('.purchase-type-digital-fee')
      );
      if (hasDigitalFee) {
        dueEl.textContent = cmrpDigitalDeliveryFeeLabel();
        dueEl.hidden = false;
      } else if (due) {
        dueEl.textContent = `Rental Due: ${due}`;
        dueEl.hidden = false;
      } else {
        dueEl.textContent = '';
        dueEl.hidden = true;
      }
    }

    summary.hidden = !this._choiceDrawerUsesInlineLayout();
  }

  _refreshChoiceSelectedSummaries() {
    if (typeof document === 'undefined') return;
    document
      .querySelectorAll('.choice-product-card.is-choice-selected')
      .forEach(item => this._syncChoiceItemChrome(item));
  }

  _formatChoicePrice(raw) {
    if (raw == null || String(raw).trim() === '') return '';
    const text = String(raw).trim();
    if (text.startsWith('$')) return text;
    const n = parseFloat(text);
    if (!isNaN(n)) return `$${n.toFixed(2)}`;
    return `$${text}`;
  }

  _isbnFromChoiceCheckbox(checkedCb, choiceItem) {
    const fromAttr = String(checkedCb?.getAttribute('data-book-isbn') || '').trim();
    if (fromAttr) return fromAttr;

    const plus = choiceItem?.querySelector('.choice-product-card__plus-select');
    const pid = plus?.getAttribute('data-product-id') || '';
    const product =
      choiceItem?.__cmrpProduct || this._getChoiceProductById(pid) || null;
    const vid = checkedCb?.getAttribute('data-variant-id');
    const variant = (product?.variants || []).find(v =>
      this._choiceIdsMatch(v?.id, vid)
    );
    if (typeof window.ProductSystem?.getVariantIsbn === 'function') {
      return String(
        window.ProductSystem.getVariantIsbn(variant, product) || ''
      ).trim();
    }
    return String(variant?.sourceIsbn || '').trim();
  }

  _updateChoiceCardSelectionDetails(choiceItem, checkedCb) {
    if (!choiceItem) return;
    const card =
      choiceItem.querySelector('.choice-product-card') || choiceItem;
    const isbnRow = card.querySelector('.choice-meta-row');
    const isbnEl = card.querySelector('.choice-meta-value');
    const priceEl = card.querySelector('.choice-product-card__price');
    const viewPriceEl = card.querySelector(
      '.choice-product-card__view-options-price'
    );

    const restoreOriginal = (el, attr) => {
      if (!el) return;
      const original = el.getAttribute(attr);
      if (original != null) el.textContent = original;
    };

    if (!checkedCb) {
      if (isbnEl) {
        const original = isbnEl.getAttribute('data-choice-original-isbn') || '';
        isbnEl.textContent = original;
        if (isbnRow) isbnRow.hidden = !original;
      }
      restoreOriginal(priceEl, 'data-choice-original-price');
      restoreOriginal(viewPriceEl, 'data-choice-original-price');
      return;
    }

    const isbn = this._isbnFromChoiceCheckbox(checkedCb, choiceItem);
    if (isbnEl) {
      if (!isbnEl.hasAttribute('data-choice-original-isbn')) {
        isbnEl.setAttribute(
          'data-choice-original-isbn',
          isbnEl.textContent || ''
        );
      }
      if (isbn) {
        isbnEl.textContent = isbn;
        if (isbnRow) isbnRow.hidden = false;
      }
    }

    const plus = choiceItem.querySelector('.choice-product-card__plus-select');
    const pid = plus?.getAttribute('data-product-id') || '';
    const product =
      choiceItem.__cmrpProduct || this._getChoiceProductById(pid) || null;
    const formatted = this._formatChoicePrice(
      this._priceFromChoiceCheckbox(checkedCb, product)
    );
    if (formatted) {
      if (priceEl) {
        if (!priceEl.hasAttribute('data-choice-original-price')) {
          priceEl.setAttribute(
            'data-choice-original-price',
            priceEl.textContent || ''
          );
        }
        priceEl.textContent = formatted;
      }
      if (viewPriceEl) {
        if (!viewPriceEl.hasAttribute('data-choice-original-price')) {
          viewPriceEl.setAttribute(
            'data-choice-original-price',
            viewPriceEl.textContent || ''
          );
        }
        viewPriceEl.textContent = formatted;
      }
    }
  }

  _syncChoiceViewOptionsButton(choiceItem) {
    const btn = choiceItem?.querySelector('.choice-product-card__view-options');
    if (!btn) return;
    const plus = choiceItem.querySelector('.choice-product-card__plus-select');
    const pid =
      plus?.getAttribute('data-product-id') ||
      btn.getAttribute('data-product-id') ||
      '';
    const panel = this._getChoiceItemPanel(choiceItem, pid);
    const open = !!panel?.classList.contains('choice-show');
    const label =
      btn.querySelector('.choice-product-card__view-options-label') ||
      btn.querySelector('span:not(.choice-product-card__view-options-price)');
    if (label) label.textContent = open ? 'Hide Options' : 'View Options';
    btn.classList.toggle('expanded', open);
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  _renumberChoiceSelections(sectionID) {
    if (!sectionID || typeof document === 'undefined') return;
    const grid = document.getElementById('choice-items-' + sectionID);
    if (!grid) return;
    const selected = [
      ...grid.querySelectorAll('.choice-item.is-choice-selected'),
    ].sort(
      (a, b) =>
        Number(a.dataset.choiceSelectSeq || 0) -
        Number(b.dataset.choiceSelectSeq || 0)
    );
    selected.forEach((item, i) => {
      const countEl = item.querySelector('.choice-plus__count');
      if (countEl) {
        countEl.textContent = String(i + 1);
        countEl.removeAttribute('hidden');
      }
    });
  }

  _updateChoiceProgressHeader(sectionID, selectedCount) {
    if (typeof document === 'undefined') return;
    const grid = sectionID
      ? document.getElementById('choice-items-' + sectionID)
      : null;
    const progress =
      (grid &&
        grid
          .closest('.choice-products-section')
          ?.querySelector('.choice-products-header__progress')) ||
      (sectionID
        ? document.getElementById('choice-progress-' + sectionID)
        : null);
    if (!progress) return;
    const min = parseInt(progress.getAttribute('data-choice-min') || '0', 10);
    const selectedCards = grid
      ? grid.querySelectorAll('.choice-item.is-choice-selected').length
      : 0;
    const count =
      selectedCount != null && Number.isFinite(Number(selectedCount))
        ? Number(selectedCount)
        : selectedCards;
    const textEl = progress.querySelector(
      '.choice-products-header__progress-text'
    );
    if (textEl) textEl.textContent = `${count}/${min} Selected`;
    progress.classList.toggle('is-empty', count <= 0);
    progress.classList.toggle('is-progress', count > 0 && count < min);
    progress.classList.toggle('is-complete', min > 0 && count >= min);
  }

  _ensureChoiceToast() {
    if (typeof document === 'undefined') return null;
    let toast = document.getElementById('choice-selection-toast');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.id = 'choice-selection-toast';
    toast.className = 'choice-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('hidden', '');
    toast.innerHTML =
      '<div class="choice-toast__accent" aria-hidden="true"></div>' +
      '<div class="choice-toast__body-row">' +
      '<div class="choice-toast__copy-wrap">' +
      '<div class="choice-toast__icon" aria-hidden="true"></div>' +
      '<div class="choice-toast__copy">' +
      '<p class="choice-toast__title"></p>' +
      '<p class="choice-toast__text"></p>' +
      '</div></div>' +
      '<button type="button" class="choice-toast__close" aria-label="Dismiss">' +
      CHOICE_TOAST_CLOSE_SVG +
      '</button></div>';
    toast.querySelector('.choice-toast__close')?.addEventListener('click', () => {
      this._hideChoiceToast();
    });
    document.body.appendChild(toast);
    return toast;
  }

  _positionChoiceToast(toast) {
    if (!toast) return;
    const bar = document.querySelector(
      'selection-bar-component.selection-bar.selection-bar--visible'
    );
    const barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : 76;
    toast.style.setProperty('--choice-toast-bar-height', `${barHeight}px`);
  }

  _hideChoiceToast() {
    if (this._choiceToastTimer) {
      clearTimeout(this._choiceToastTimer);
      this._choiceToastTimer = null;
    }
    const toast = document.getElementById('choice-selection-toast');
    if (!toast) return;
    toast.classList.remove('is-visible');
    toast.setAttribute('hidden', '');
  }

  _showChoiceToast({ kind, title, body }) {
    const toast = this._ensureChoiceToast();
    if (!toast) return;

    const isInfo = kind === 'exceeded';
    toast.classList.toggle('choice-toast--info', isInfo);
    toast.classList.toggle('choice-toast--success', !isInfo);

    const icon = toast.querySelector('.choice-toast__icon');
    if (icon) icon.innerHTML = isInfo ? CHOICE_TOAST_INFO_SVG : CHOICE_TOAST_CHECK_SVG;

    const titleEl = toast.querySelector('.choice-toast__title');
    const textEl = toast.querySelector('.choice-toast__text');
    if (titleEl) titleEl.textContent = title;
    if (textEl) textEl.textContent = body;

    this._positionChoiceToast(toast);
    toast.removeAttribute('hidden');
    requestAnimationFrame(() => toast.classList.add('is-visible'));

    if (this._choiceToastTimer) clearTimeout(this._choiceToastTimer);
    this._choiceToastTimer = setTimeout(() => {
      this._hideChoiceToast();
    }, CHOICE_TOAST_DURATION_MS);
  }

  _syncChoiceSelectionToast(sectionID, count, minRequired) {
    if (!sectionID || minRequired <= 0) return;

    const prev = this._choiceToastPrevCounts[sectionID];
    this._choiceToastPrevCounts[sectionID] = count;

    if (prev === undefined) return;

    const inline = this._choiceDrawerUsesInlineLayout();
    if (count === minRequired && prev < minRequired) {
      this._showChoiceToast({
        kind: 'complete',
        title: 'Way to go!',
        body: `You've selected ${count}/${minRequired} materials.`,
      });
      return;
    }
    if (count > minRequired && prev <= minRequired) {
      this._showChoiceToast({
        kind: 'exceeded',
        title: inline ? 'Heads up.' : 'Heads Up',
        body: "You've selected more items than your instructor requires for this course.",
      });
      return;
    }
    if (count < minRequired || (count === minRequired && prev > minRequired)) {
      this._hideChoiceToast();
    }
  }

  _openChoicePurchaseOptions(productId, options = {}) {
    if (!productId) return;
    const toggle = options.toggle === true;
    // Desktop always uses the Preact drawer. Never open the legacy HTML sheet.
    if (!this._choiceDrawerUsesInlineLayout()) {
      this._tryOpenChoiceVariantDrawer(productId, { toggle });
      return;
    }
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productId
    );
    const checkBtn = document.getElementById('check-box-product-' + productId);
    if (!choiceInfo || !checkBtn) return;
    if (choiceInfo.classList.contains('choice-show')) {
      if (toggle) {
        this._closeChoicePurchaseOptions(productId);
      }
      return;
    }
    checkBtn.classList.remove('expanded');
    this.toggleViewMoreChoice(checkBtn, 'checkbox');
  }

  _choiceProductIdFromDetailsPanel(panel) {
    if (!panel?.id) return '';
    return String(panel.id).replace(/^choice-details-product-/, '');
  }

  _choiceIdsMatch(a, b) {
    if (a == null || b == null) return false;
    const na = String(a).split('/').pop();
    const nb = String(b).split('/').pop();
    return na === nb;
  }

  _getChoiceSectionIdForProduct(productId) {
    const cb = document.getElementById('check-box-product-' + productId);
    return cb?.getAttribute('data-section-id') || null;
  }

  _getChoiceProductById(productId) {
    if (!productId) return null;
    const plus = document.querySelector(
      `.choice-product-card__plus-select[data-product-id="${productId}"]`
    );
    const item =
      plus?.closest('.choice-item') || plus?.closest('.material-item') || null;
    if (item?.__cmrpProduct) return item.__cmrpProduct;

    const components = document.querySelectorAll('choice-products-component');
    for (const el of components) {
      try {
        const raw =
          el.dataset.cmrpProducts || el.getAttribute('products-data') || '[]';
        const list = JSON.parse(raw);
        if (!Array.isArray(list)) continue;
        const found = list.find(product =>
          this._choiceIdsMatch(product?.id, productId)
        );
        if (found) return found;
      } catch (e) {
        // Ignore malformed products-data on a choice component instance.
      }
    }
    return null;
  }

  _tryOpenChoiceVariantDrawer(productId, options = {}) {
    if (this._choiceDrawerUsesInlineLayout()) return false;
    const product = this._getChoiceProductById(productId);
    if (!product) return false;
    const detail = {
      product,
      productId,
      sectionId: this._getChoiceSectionIdForProduct(productId),
      toggle: options.toggle === true,
      handled: false,
    };
    window.dispatchEvent(
      new CustomEvent('cmrp:openChoiceVariantDrawer', { detail })
    );
    return !!detail.handled;
  }

  _closeChoicePurchaseOptions(productId) {
    if (!productId) return;
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productId
    );
    if (!choiceInfo || !choiceInfo.classList.contains('choice-show')) return;
    const card = this._choiceDrawerUsesInlineLayout()
      ? choiceInfo.closest('.choice-item')?.querySelector('.choice-product-card')
      : null;
    const active = document.activeElement;
    if (active instanceof HTMLElement && choiceInfo.contains(active)) {
      active.blur();
    }
    cmrpChoiceReleaseFocusTrap(true);
    cmrpKeepViewportTop(card, () => {
      choiceInfo.classList.add('choice-hide');
      choiceInfo.classList.remove('choice-show');
      this._portalChoiceDrawerClose(choiceInfo);
      const checkBtn = document.getElementById('check-box-product-' + productId);
      checkBtn?.classList.remove('expanded');
      this._syncChoiceDrawerScrollLock();
      this._updateChoiceProductPlusButton(productId);
    });
  }

  _removeChoiceSelection(productId) {
    if (!productId) return;
    const suffix = String(productId).split('/').pop();
    const panel =
      document.getElementById('choice-details-product-' + productId) ||
      document.getElementById('choice-details-product-' + suffix);
    const card = this._choiceDrawerUsesInlineLayout()
      ? (panel?.closest('.choice-item') || panel?.__cmrpChoiceOuter)?.querySelector(
          '.choice-product-card'
        )
      : null;
    cmrpKeepViewportTop(card, () => {
      const checked = panel
        ? panel.querySelectorAll('.purchase-checkbox:checked')
        : [];
      checked.forEach(cb => {
        cb.checked = false;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const rowCb =
        document.getElementById('check-box-product-' + productId) ||
        document.getElementById('check-box-product-' + suffix);
      if (rowCb) {
        rowCb.checked = false;
        rowCb.classList.remove('expanded');
      }
      this._closeChoicePurchaseOptions(productId);
      if (suffix !== String(productId)) {
        this._closeChoicePurchaseOptions(suffix);
      }
      this._updateChoiceProductPlusButton(productId);
      if (suffix !== String(productId)) {
        this._updateChoiceProductPlusButton(suffix);
      }
    });
  }

  _bindChoiceCardActions(materialItem, productId) {
    if (!materialItem || !productId) return;
    const card = materialItem.querySelector('.choice-product-card') || materialItem;
    if (card.dataset.choiceActionsBound === '1') return;
    card.dataset.choiceActionsBound = '1';

    const viewOptions = card.querySelector('.choice-product-card__view-options');
    const editBtn = card.querySelector('.choice-product-card__edit');
    const removeBtn = card.querySelector('.choice-product-card__remove');

    const openOptions = e => {
      e.preventDefault();
      e.stopPropagation();
      this._openChoicePurchaseOptions(productId);
    };
    const toggleOptions = e => {
      e.preventDefault();
      e.stopPropagation();
      this._openChoicePurchaseOptions(productId, { toggle: true });
    };

    if (viewOptions) {
      viewOptions.addEventListener('click', toggleOptions);
    }
    if (editBtn) {
      editBtn.addEventListener('click', openOptions);
    }
    if (removeBtn) {
      removeBtn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this._removeChoiceSelection(productId);
      });
    }
  }

  updateProducts(newProducts) {
    this.products = newProducts;
  }

  updateProductHandler(newProductHandler) {
    this.productHandler = newProductHandler;
  }

  setupCartEventListeners() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.addEventListener(event, this.handleCartUpdate)
    );
  }

  async handleCartUpdate() {
    try {
      if (!document.querySelector('.material-item')) return;
      await new Promise(resolve => setTimeout(resolve, 300));
      await this.updateAllInBagIndicators();
    } catch (error) {}
  }

  setCallbacks(callbacks) {
    if (callbacks.updateSelectedProducts) {
      this.updateSelectedProductsCallback = callbacks.updateSelectedProducts;
    }
    if (callbacks.updateSelectionBarSummary) {
      this.updateSelectionBarSummaryCallback =
        callbacks.updateSelectionBarSummary;
    }
  }

  handleFormatSelection(button, materialItem, product) {
    const selectedFormat = button.getAttribute('data-format');

    materialItem
      .querySelectorAll('.format-btn')
      .forEach(btn => btn.classList.remove('selected'));
    button.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const deliveryNotice = materialItem.querySelector(
      '.delivery-notice[data-format-specific="digital"]'
    );
    if (deliveryNotice) {
      deliveryNotice.style.display =
        selectedFormat === 'digital' ? 'block' : 'none';
    }

    if (selectedFormat) {
      this.updatePricingForFormat(materialItem, selectedFormat, product);
    }
  }

  updatePricingForFormat(materialItem, format, product) {
    const purchaseTypes = materialItem.querySelector('.purchase-types');
    if (!purchaseTypes) return;

    const courseCard = materialItem.closest('.course-card');
    const sectionData = {
      courseInfo: {
        selectedsectionId: courseCard?.dataset?.selectedSectionId || '',
      },
    };

    purchaseTypes.innerHTML = '';
    let hasAvailableOptions = false;
    const variantsByType = {};

    const filteredVariants =
      product.variants?.filter(variant =>
        variant.option1?.toLowerCase().includes(format.toLowerCase())
      ) || [];

    filteredVariants.forEach(variant => {
      const type = variant.option2 || 'Buy';
      if (!variantsByType[type]) variantsByType[type] = [];
      variantsByType[type].push(variant);
    });

    const purchaseTypeOrder = String(format || '')
      .toLowerCase()
      .includes('digital')
      ? ['rent', 'buy']
      : ['rent used', 'rent new', 'buy used', 'buy new'];
    const purchaseTypeSortIndex = option2 => {
      const lower = (option2 || '').toLowerCase().trim();
      for (let i = 0; i < purchaseTypeOrder.length; i++) {
        if (lower.indexOf(purchaseTypeOrder[i]) !== -1) return i;
      }
      return 999;
    };
    const sortedTypes = Object.keys(variantsByType).sort(
      (a, b) => purchaseTypeSortIndex(a) - purchaseTypeSortIndex(b)
    );
    const sortVariantsByPrice = variants =>
      variants.slice().sort((a, b) => {
        const pa = parseFloat(a?.price);
        const pb = parseFloat(b?.price);
        return (isNaN(pa) ? 0 : pa) - (isNaN(pb) ? 0 : pb);
      });

    sortedTypes.forEach(type => {
      const typeVariants = sortVariantsByPrice(variantsByType[type] || []);
      typeVariants.forEach(variant => {
        if (!variant) return;

        const price = parseFloat(variant.price).toFixed(2);
        const productId = product.title.replace(/\s+/g, '-').toLowerCase();
        const typeId = type.toLowerCase().replace(/\s+/g, '-');
        const isAvailable = variant.available;

        const mfa = Array.isArray(variant?.metafields) ? variant.metafields : [];
        const bookISBN =
          (typeof window.ProductSystem?.getVariantIsbn === 'function'
            ? window.ProductSystem.getVariantIsbn(variant, product)
            : '') ||
          mfa.find(metafield => metafield.isbn_ean)?.isbn_ean ||
          variant?.sourceIsbn ||
          '';
        const bookEdition =
          mfa.find(metafield => metafield.edition)?.edition ||
          product?.metafields?.edition ||
          product?.metafields?.course_materials?.edition ||
          '';
        const bookCopyright = mfa.find(metafield => metafield.copyright_year)?.copyright_year || '';
        const bookPublisher = mfa.find(metafield => metafield.publisher_name)?.publisher_name || '';
        const bookRentDueDate = mfa.find(metafield => metafield.expiration_date)?.expiration_date || '';


        const taxonomy_dep = mfa.find(metafield => 
            metafield.department && metafield.namespace === "taxonomy"
        )?.department || '';

        const taxonomy_sub_dep = mfa.find(metafield => 
            metafield.sub_department && metafield.namespace === "taxonomy"
        )?.sub_department || '';

        const taxonomy_class = mfa.find(metafield => 
            metafield.class && metafield.namespace === "taxonomy"
        )?.class || '';

        const taxonomy_sub_class = mfa.find(metafield => 
            metafield.sub_class && metafield.namespace === "taxonomy"
        )?.sub_class || '';

        const rental_only_sku = mfa.find(metafield => 
            metafield.rental_only_sku && metafield.namespace === "cm_rental"
        )?.rental_only_sku || '';

        if (isAvailable) hasAvailableOptions = true;

        const availabilityClass = isAvailable ? '' : ' unavailable';

        const subscriptionLength = mfa.find(metafield => metafield.subscription)?.subscription || '';

        const sectionId =
          String(sectionData?.courseInfo?.selectedsectionId || '').trim() ||
          String(
            materialItem?.closest('.course-card')?.dataset?.selectedSectionId ||
              ''
          ).trim() ||
          String(materialItem?.dataset?.purchaseInstanceId || '').trim();
        purchaseTypes.innerHTML += this._generatePurchaseTypeHTML(
          type,
          typeId,
          productId,
          variant,
          price,
          availabilityClass,
          product,
          bookISBN,
          bookEdition,
          bookCopyright,
          bookPublisher,
          bookRentDueDate,
          taxonomy_dep,
          taxonomy_sub_dep,
          taxonomy_class,
          taxonomy_sub_class,
          rental_only_sku,
          subscriptionLength,
          sectionId,
          sectionData
        );
      });
    });

    if (!hasAvailableOptions && Object.keys(variantsByType).length === 0) {
      purchaseTypes.innerHTML = `
        <div class="no-options-message" style="text-align: center; padding: 20px; color: #666;">
          <p>No purchase options available for this format, yet.</p>
          <p style="font-size: 0.875rem; margin-top: 8px;">Please check back in later.</p>
        </div>
      `;
    }

    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      format,
      product
    );
    this._updateInBagIndicators(materialItem);

    const collapseContent = purchaseTypes.closest('.collapse-content');
    if (collapseContent) {
      syncCmrpVariantNoticeInCollapse(collapseContent);
    }
    this._updateMultiFormatWarning(materialItem);
  }

  _generatePurchaseTypeHTML(
    type,
    typeId,
    productId,
    variant,
    price,
    availabilityClass,
    product,
    bookISBN,
    bookEdition,
    bookCopyright,
    bookPublisher,
    bookRentDueDate,
    taxonomy_dep,
    taxonomy_sub_dep,
    taxonomy_class,
    taxonomy_sub_class,
    rental_only_sku,
    subscriptionLength,
    sectionId = '',
    sectionData = { courseInfo: {} }
  ) {
    const formatLower = String(variant?.option1 || '').toLowerCase();
    const typeLower = type.toLowerCase();
    const printVariantNoticeEligible =
      formatLower === 'print' &&
      (typeLower.includes('buy used') ||
        typeLower.includes('rent used') ||
        (typeLower.includes('used') &&
          (typeLower.includes('buy') || typeLower.includes('rent'))));
    const variantNoticeAttr = printVariantNoticeEligible
      ? ` data-variant-notice="${cmrpEscapeVariantNoticeAttr(cmrpUsedPrintVariantNoticeText())}"`
      : '';

    const isDigitalStr =
      window.ProductSystem?.getVariantIsDigitalString?.(variant, product) || '';
    const publisher_code =
      window.ProductSystem?.getVariantPublisherCode?.(variant, product) || '';

    const hasDigitalRentDuration =
      String(isDigitalStr).toLowerCase() === 'true' &&
      typeLower.includes('rent') &&
      subscriptionLength !== '' &&
      subscriptionLength != null &&
      Number(subscriptionLength) > 0;
    const displayTypeName = hasDigitalRentDuration
      ? `${subscriptionLength} Day Rental`
      : String(type);

    const variantValueId = typeof variant.id === 'string' ? variant.id.split('/').pop() : variant.id;
    const sectionSuffix = sectionId ? String(sectionId).replace(/[^a-zA-Z0-9-]/g, '_') : '';
    const checkboxId = sectionSuffix
      ? `${typeId}-${productId}-${variantValueId}-${sectionSuffix}`
      : `${typeId}-${productId}-${variantValueId}`;

    let hidePurchaseSalesChannel = false;
    if (window.ProductSystem?.courseMaterialsShouldHideBlockedAccessSku) {
      hidePurchaseSalesChannel = window.ProductSystem.courseMaterialsShouldHideBlockedAccessSku(
        product,
        sectionData,
        variant
      );
    }
    if (
      !hidePurchaseSalesChannel &&
      formatLower.includes('digital') &&
      window.ProductSystem?.courseMaterialsShouldHideDigitalPurchase
    ) {
      hidePurchaseSalesChannel = window.ProductSystem.courseMaterialsShouldHideDigitalPurchase(
        product,
        sectionData,
        variant,
        isDigitalStr
      );
    }
    const discoverViewNoSelection =
      window.ProductSystem?.isFollettDiscoverViewPage?.() === true;
    // OOS rows still show the checkmark UI; the input is disabled when unavailable.
    const showCheckbox =
      !discoverViewNoSelection && !hidePurchaseSalesChannel;
    const showPrice = !hidePurchaseSalesChannel;
    const isUnavailable = availabilityClass.includes('unavailable');
    const hideOosNotifyOnCmrp =
      document.body?.classList?.contains('course-materials-page') === true;
    const oosNotifyClick =
      isUnavailable && !hideOosNotifyOnCmrp
        ? `onclick='event.stopPropagation(); window.handleNotifyMe(${JSON.stringify(variant.id)}, ${JSON.stringify(product.title)}, ${JSON.stringify(type)});'`
        : '';
    const oosExtra = isUnavailable
      ? hideOosNotifyOnCmrp
        ? `<div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>
        <div class="purchase-price out-of-stock-overlay"> Out of Stock </div>`
        : `
        <div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>
        <div class="purchase-price out-of-stock-overlay"> Out of Stock </div>
        <div class="purchase-type__oos-foot">
          <button type="button" class="purchase-type__notify-btn" ${oosNotifyClick}>
            <svg class="purchase-type__notify-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M3 7l9 6 9-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Notify Me</span>
          </button>
        </div>`
      : '';
    return `
      <div class="purchase-type${availabilityClass}"${variantNoticeAttr}
           data-purchase-type="${typeId}" 
           data-variant-id="${variant.id}"
           onclick="window.togglePurchaseOption(this, event)">
        <div class="purchase-type-header">
          <span class="purchase-type-label">${displayTypeName}</span>
          <div class="purchase-price">$${price}</div>
          ${
            showCheckbox
              ? `<div class="purchase-checkbox-container" onclick="event.stopPropagation()">
            <input type="checkbox" 
                   id="${checkboxId}" 
                   class="purchase-checkbox" 
                   aria-label="${cmrpEscapeVariantNoticeAttr([product.title, String(isDigitalStr).toLowerCase() === 'true' ? 'Digital' : 'Physical', displayTypeName, `$${price}`].filter(Boolean).join(', '))}"
                   data-format="${variant.option1}"
                   data-type="${type}"
                   data-product-id="${cmrpResolveDisplayProductId(product, variant)}"
                   data-purchase-type="${typeId}"
                   data-variant-id="${variant.id}"
                   data-variant-price="${price}"
                   data-book-isbn="${bookISBN}"
                   data-book-edition="${bookEdition}"
                   data-book-copyright="${bookCopyright}"
                   data-book-publisher="${bookPublisher}"
                   data-book-rent-due-date="${bookRentDueDate}"
                   data-department-taxonomy="${taxonomy_dep}"
                   data-sub-department="${taxonomy_sub_dep}"
                   data-class="${taxonomy_class}"
                   data-sub-class="${taxonomy_sub_class}"
                   data-product-title="${product.title.replace(/'/g, "\\'")}"
                   data-rental-only-sku="${rental_only_sku}"
                   data-subscription-length="${subscriptionLength}"
                   data-section-id="${sectionId}"
                   data-is-digital="${String(isDigitalStr).toLowerCase() === 'true' ? 'true' : 'false'}"
                   data-publisher-code="${publisher_code}"
                   onchange="window.handlePurchaseSelection(this)"${isUnavailable ? ' disabled' : ''}>
            <label for="${checkboxId}" class="checkbox-label" aria-hidden="true"></label>
          </div>`
              : ''
          }
        </div>
        ${oosExtra}
      </div>
    `;
  }

  _updateNotifyMeVisibility(
    materialItem,
    hasAvailableOptions,
    format,
    product
  ) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    notifyContainer.style.display = 'none';
    notifyContainer.innerHTML = '';

    const attributes = {
      'data-product-id': product.id,
      'data-product-title': product.title,
      'data-format': format,
      'data-has-available-options': hasAvailableOptions.toString(),
    };

    Object.entries(attributes).forEach(([key, value]) => {
      notifyContainer.setAttribute(key, value);
    });
  }

  _checkSimpleProductNotifyMe(materialItem, product) {
    const purchaseOptions = materialItem.querySelectorAll(
      '.purchase-type:not(.unavailable)'
    );
    const hasAvailableOptions = purchaseOptions.length > 0;
    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      'simple',
      product
    );
  }

  togglePurchaseOption(purchaseTypeDiv, event) {
    const isDiscoverView =
      window.ProductSystem?.isFollettDiscoverViewPage?.() === true;
    const isUnavailable = purchaseTypeDiv.classList.contains('unavailable');
    if (isDiscoverView && !isUnavailable) return;
    if (event?.target && event.target.closest('.purchase-checkbox-container'))
      return;
    if (event?.target?.closest?.('.requirements-link')) return;

    if (isUnavailable) {
      if (event?.target?.closest?.('.purchase-type__notify-btn')) return;
      if (
        event?.target?.closest?.(
          'a, button, .requirements-link, anchored-popover-component',
        )
      ) {
        return;
      }
      event?.preventDefault?.();
      event?.stopPropagation?.();
      return;
    }

    const checkbox = purchaseTypeDiv.querySelector('.purchase-checkbox');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    }
  }
  handleMobileSelection(plusboxSelection, productID) {
    if (!plusboxSelection) return;
    if (plusboxSelection.classList.contains('choice-product-card__plus-select')) {
      this._updateChoiceProductPlusButton(productID);
      return;
    }
    const materialItem = plusboxSelection.closest('.material-item');
    let selectedCount = 0;
    materialItem
      ?.querySelectorAll('.purchase-checkbox:checked')
      .forEach(() => {
        selectedCount += 1;
      });
    plusboxSelection.innerHTML = selectedCount > 0
      ? String(selectedCount)
      : `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  updateViewMoreDetailsChoice(productID, sectionScope){
    const scope = sectionScope || document;
    const buttonViewCheck = scope.querySelector("#choice-info-product-"+productID);
    if (!buttonViewCheck) return;
    const text = 'View details';
    const rotation = '0deg';
    const spanElement = buttonViewCheck.querySelector('span');
    if (spanElement) spanElement.textContent = text;
    const chevron = buttonViewCheck.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    if (buttonViewCheck.classList.contains("expanded")) {
      buttonViewCheck.classList.remove("expanded");
    }
    this._updateChoiceProductPlusButton(productID);
  }
  hideShowPriceChoice(range,price,productID,sectionScope){
    const scope = sectionScope || document;
    if(range){
      const rangeChoice = scope.querySelector('#price-range-choice-'+productID);
      if (rangeChoice) {
        rangeChoice.classList.add("show-price-choice");
        rangeChoice.classList.remove("hide-price-choice");
      }
      const priceChoice = scope.querySelector('#price-choice-'+productID);
      if (priceChoice) {
        priceChoice.classList.add("hide-price-choice");
        priceChoice.classList.remove("show-price-choice");
      }
    }else{
      const priceChoice = scope.querySelector('#price-choice-'+productID);
      if (priceChoice) {
        priceChoice.classList.add("show-price-choice");
        priceChoice.classList.remove("hide-price-choice");
        priceChoice.innerText = "$"+price;
      }
      const rangeChoice = scope.querySelector('#price-range-choice-'+productID);
      if (rangeChoice) {
        rangeChoice.classList.add("hide-price-choice");
        rangeChoice.classList.remove("show-price-choice");
      }
    }
  }

  _priceFromChoiceCheckbox(checked, product) {
    const raw = checked?.getAttribute('data-variant-price');
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();
    const vid = checked?.getAttribute('data-variant-id');
    if (!vid || !product?.variants) return '';
    const variant = product.variants.find(v =>
      this._choiceIdsMatch(v?.id, vid)
    );
    if (variant?.price == null || variant.price === '') return '';
    return String(variant.price);
  }

  /**
   * One entry per Choice *card* with a checked purchase option (not unique product id).
   */
  _collectSelectedChoiceProducts(sectionID) {
    const selectedChoices = [];
    const selectedProducts = [];
    const selectedPrices = [];
    if (!sectionID || typeof document === 'undefined') {
      return { selectedChoices, selectedProducts, selectedPrices };
    }

    const grid = document.getElementById('choice-items-' + sectionID);
    if (!grid) return { selectedChoices, selectedProducts, selectedPrices };

    grid.querySelectorAll('.choice-item').forEach(item => {
      this._syncChoiceItemChrome(item);
      const plus = item.querySelector('.choice-product-card__plus-select');
      const pid = plus?.getAttribute('data-product-id') || '';
      const pan = this._getChoiceItemPanel(item, pid);
      const checked = this._getCheckedChoiceCheckbox(pan, pid);
      const selected = item.classList.contains('is-choice-selected');
      if (!selected) return;
      const product =
        item.__cmrpProduct || this._getChoiceProductById(pid) || null;
      selectedChoices.push(pid || pan?.id || String(selectedChoices.length));
      if (product) selectedProducts.push(product);
      const price = this._priceFromChoiceCheckbox(checked, product);
      if (price) selectedPrices.push(price);
    });

    return { selectedChoices, selectedProducts, selectedPrices };
  }

  _publishChoiceCollapsedSummary(sectionID, selectedProducts, selectedPrices) {
    const bySection = window.cmrpChoiceSummaryUpdaters;
    const updater = bySection && sectionID ? bySection[sectionID] : null;
    if (typeof updater === 'function') {
      updater(selectedProducts, selectedPrices);
      return;
    }
    if (typeof window.updateChoiceProducts === 'function') {
      window.updateChoiceProducts(selectedProducts);
    }
    if (typeof window.updatePriceData === 'function') {
      window.updatePriceData(selectedPrices);
    }
  }

  /**
   * Desktop: swap the product grid for the collapsed cover summary once the
   * required count is met. Inline (≤968px) always keeps the stacked list.
   */
  _applyChoiceCollapsedVisibility(sectionID, collapse) {
    const choiceItems = document.getElementById('choice-items-' + sectionID);
    const choiceFinalItems = document.getElementById(
      'choice-final-items-' + sectionID
    );
    const useCollapsed =
      collapse && !this._choiceDrawerUsesInlineLayout() && !!choiceFinalItems;

    if (useCollapsed) {
      choiceItems?.classList.add('hide-choice-items');
      choiceItems?.classList.remove('show-choice-items');
      choiceFinalItems.classList.add('show-choice-items');
      choiceFinalItems.classList.remove('hide-choice-items');
    } else {
      choiceItems?.classList.remove('hide-choice-items');
      choiceItems?.classList.add('show-choice-items');
      choiceFinalItems?.classList.remove('show-choice-items');
      choiceFinalItems?.classList.add('hide-choice-items');
    }
  }

  /**
   * Choice section: grid swap and selection toasts from current DOM selections.
   */
  _syncChoiceMinimumAndExtraData(sectionID) {
    if (!sectionID || typeof document === 'undefined') return;

    const { selectedChoices, selectedProducts, selectedPrices } =
      this._collectSelectedChoiceProducts(sectionID);

    this._updateChoiceProgressHeader(sectionID, selectedChoices.length);
    this._renumberChoiceSelections(sectionID);

    const choice_minimum = document.getElementById('choice-minimum-' + sectionID);
    if (!choice_minimum) return;

    const minRequired = parseInt(choice_minimum.value, 10) || 0;

    if (selectedChoices.length >= minRequired && minRequired > 0) {
      this._publishChoiceCollapsedSummary(
        sectionID,
        selectedProducts,
        selectedPrices
      );
      this._applyChoiceCollapsedVisibility(
        sectionID,
        selectedChoices.length >= minRequired && minRequired > 0
      );
    } else {
      this._publishChoiceCollapsedSummary(sectionID, [], []);
      this._applyChoiceCollapsedVisibility(sectionID, false);
    }

    this._syncChoiceSelectionToast(
      sectionID,
      selectedChoices.length,
      minRequired
    );
    this._updateChoiceProgressHeader(sectionID, selectedChoices.length);
    this._renumberChoiceSelections(sectionID);
  }

  handlePurchaseSelection(checkbox) {
    const materialItem = checkbox.closest('.material-item');
    if (!materialItem) return;
    const formatBtns = materialItem.querySelectorAll('.format-btn');
    if (formatBtns.length > 0 && !this.getSelectedFormat(materialItem)) {
      alert('Please select a format (Digital or Print) first.');
      checkbox.checked = false;
      cmrpSyncPurchaseTypeSelectedClass(materialItem);
      return;
    }
    const isMobile = window.innerWidth <= 786;

    const cardProductId = cmrpResolveChoiceCardProductId(materialItem);
    const productID =
      cardProductId || checkbox.getAttribute('data-product-id');
    const sectionID = checkbox.getAttribute('data-section-id');
    const price = checkbox.getAttribute('data-variant-price');
    const sectionScope = this._getSectionScope(checkbox);
    const checkboxChoice = sectionScope.querySelector("#check-box-product-"+productID);
    const plusboxSelection = materialItem.querySelector(
      '.choice-product-card__plus-select, .material-image-plus-button'
    );
    const divDetails = sectionScope.querySelector("#div-view-details-"+productID);
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productID
    );

    // Non-react CMRP cards now allow only one selected variant per product.
    // If a variant is checked, clear any other checked variant for the same product
    // (including variants rendered under a different format tab).
    if (checkbox.checked) {
      // Enforce a single selected variant per rendered product card.
      // Scope to the current material item so this works even when variant rows
      // across tabs carry different product IDs (e.g. merged/unified data).
      const otherCheckedInCard = materialItem.querySelectorAll(
        '.purchase-checkbox:checked'
      );
      otherCheckedInCard.forEach(otherCheckbox => {
        if (otherCheckbox === checkbox) return;
        otherCheckbox.checked = false;
      });
    }
    cmrpSyncPurchaseTypeSelectedClass(materialItem);
    if(divDetails && !isMobile){
      if(checkbox.checked){
        divDetails.classList.remove("material-view-more-choice");
        if (choiceInfo) {
          choiceInfo.classList.add("choice-hide");
          choiceInfo.classList.remove("choice-show");
          this._portalChoiceDrawerClose(choiceInfo);
        }
        this.updateViewMoreDetailsChoice(productID, sectionScope);


        const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
        let selectedCount = 0; 
        elementsCheck.forEach(element => {           
          if (element.checked) { 
            selectedCount++; 
          }
        });
        
        const isAnySelected = selectedCount >= 2; 
        this.hideShowPriceChoice(isAnySelected,price,productID,sectionScope);

      }else{
        if(checkboxChoice){
          const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
          let isAnySelected = false;
          elementsCheck.forEach(element => {           
            if (element.selected || element.checked) { 
              isAnySelected = true; 
            }
          });
          if(!isAnySelected){
            divDetails.classList.add("material-view-more-choice");
          }else{
            divDetails.classList.remove("material-view-more-choice");
            if (choiceInfo) {
              choiceInfo.classList.add("choice-hide");
              choiceInfo.classList.remove("choice-show");
              this._portalChoiceDrawerClose(choiceInfo);
            }
            this.updateViewMoreDetailsChoice(productID, sectionScope);
          }
          
        }

        const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
        let selectedCount = 0;
        let priceProductSelected = '' 
        elementsCheck.forEach(element => {           
          if (element.checked) { 
            selectedCount++; 
            priceProductSelected = element.getAttribute('data-variant-price');
          }
        });
        
        const isAnySelected = selectedCount >= 2;
        this.hideShowPriceChoice(isAnySelected,priceProductSelected,productID,sectionScope);
      }

    }
    
    
    if (this._choiceDrawerUsesInlineLayout() && productID) {
      this.handleMobileSelection(plusboxSelection, productID);
      if (checkbox.checked) {
        this._closeChoicePurchaseOptions(productID);
      }
    } else if (plusboxSelection && isMobile) {
      this.handleMobileSelection(plusboxSelection, productID);
    }
    
    

    const purchaseTypeDiv = checkbox.closest('.purchase-type');
    const isUnavailable = purchaseTypeDiv?.classList.contains('unavailable');

    this._updateNotifyMeButton(
      materialItem,
      isUnavailable && checkbox.checked ? checkbox : undefined
    );

    const selectionManager =
      window.courseMaterialsPage?.components?.selectionManager;
    if (
      checkbox.closest('[id^="choice-details-product-"]') &&
      typeof selectionManager?.syncChoiceProductFromCheckbox === 'function'
    ) {
      selectionManager.syncChoiceProductFromCheckbox(checkbox);
      if (typeof selectionManager.syncSuggestedMaterials === 'function') {
        selectionManager.syncSuggestedMaterials();
      }
    }

    if (!isUnavailable) {
      this.updateSelectedProductsCallback();
      this.updateSelectionBarSummaryCallback();
    }

    this._updateMultiFormatWarning(materialItem);

    const collapseForNotice = checkbox.closest('.collapse-content');
    if (collapseForNotice) {
      syncCmrpVariantNoticeInCollapse(collapseForNotice);
    }
    if (checkbox.checked) {
      materialItem.querySelectorAll('.purchase-checkbox').forEach(cb => {
        const collapse = cb.closest('.collapse-content');
        if (collapse) {
          syncCmrpVariantNoticeInCollapse(collapse);
        }
      });
    }

    if (productID) {
      this._updateChoiceProductPlusButton(productID);
    }

    const choiceSectionId = this._resolveChoiceSectionId(checkbox, sectionID);
    if (
      choiceSectionId &&
      document.getElementById('choice-minimum-' + choiceSectionId)
    ) {
      this._syncChoiceMinimumAndExtraData(choiceSectionId);
    }

    this._syncChoiceDrawerScrollLock();
  }

  _updateNotifyMeButton(materialItem, selectedCheckbox = undefined) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    if (document.body?.classList?.contains('course-materials-page')) {
      notifyContainer.style.display = 'none';
      notifyContainer.innerHTML = '';
      return;
    }

    const selectedUnavailableItems = materialItem.querySelectorAll(
      '.purchase-type.unavailable .purchase-checkbox:checked'
    );

    if (selectedUnavailableItems.length === 0) {
      notifyContainer.style.display = 'none';
      notifyContainer.innerHTML = '';
      return;
    }

    const contextCheckbox =
      selectedCheckbox ||
      selectedUnavailableItems[selectedUnavailableItems.length - 1];
    if (!contextCheckbox) return;

    const productTitle = notifyContainer.getAttribute('data-product-title');
    const format = notifyContainer.getAttribute('data-format');
    const variantId = contextCheckbox.getAttribute('data-variant-id');

    notifyContainer.style.display = 'block';
    notifyContainer.innerHTML = `
      <div class="notify-me-btn" onclick="handleNotifyMe('${variantId}', '${productTitle}', '${format}')">
        <svg class="notify-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 4v4M8 12h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="notify-text"><span class="underlined-text">Notify me</span> when it's back in stock.</span>
      </div>
    `;
  }

  addMaterialEventListeners(materialItem, product) {
    if (materialItem && product) {
      materialItem.__cmrpProduct = product;
    }

    const viewMoreBtn = materialItem.querySelector('.view-more-btn');
    if (viewMoreBtn) {
      viewMoreBtn.addEventListener('click', () =>
        this.toggleViewMore(viewMoreBtn)
      );
    }

    const viewMoreBtnChoice = materialItem.querySelector('.view-more-btn-choice');
    if (viewMoreBtnChoice) {
      viewMoreBtnChoice.addEventListener('click', (e) => {
        const pid = this._choiceProductIdFromToggleButton(viewMoreBtnChoice);
        if (pid && !this._choiceDrawerUsesInlineLayout()) {
          this._tryOpenChoiceVariantDrawer(pid, { toggle: true });
          return;
        }
        this.toggleViewMoreChoice(viewMoreBtnChoice,'btn')
      });
    }

    const choiceDetailsPanel = materialItem.querySelector(
      '[id^="choice-details-product-"]'
    );
    if (choiceDetailsPanel) {
      const backdrop = choiceDetailsPanel.querySelector('.choice-drawer-backdrop');
      const closeBtn = choiceDetailsPanel.querySelector('.choice-drawer-close');
      const collapseOpenChoiceDrawer = () => {
        const viewMoreBtn = materialItem.querySelector('.view-more-btn-choice');
        const checkBtn = materialItem.querySelector('.view-check-btn-choice');
        if (viewMoreBtn?.classList.contains('expanded')) {
          this.toggleViewMoreChoice(viewMoreBtn, 'btn');
        } else if (checkBtn?.classList.contains('expanded')) {
          this.toggleViewMoreChoice(checkBtn, 'checkbox');
        }
      };
      if (backdrop) {
        backdrop.addEventListener('click', () => collapseOpenChoiceDrawer());
      }
      if (closeBtn) {
        closeBtn.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          collapseOpenChoiceDrawer();
        });
      }
    }

    const viewCheckBtnChoice = materialItem.querySelector('.view-check-btn-choice');
    if (viewCheckBtnChoice) {
      viewCheckBtnChoice.addEventListener('click', () => {
        const pid = this._choiceProductIdFromToggleButton(viewCheckBtnChoice);
        this._openChoicePurchaseOptions(pid, { toggle: true });
      });
    }

    const choicePlus = materialItem.querySelector(
      '.choice-product-card__plus-select'
    );
    if (choicePlus) {
      const pid = choicePlus.getAttribute('data-product-id');
      const openDrawer = () => {
        this._openChoicePurchaseOptions(pid, { toggle: true });
      };
      choicePlus.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      });
      choicePlus.addEventListener('keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        openDrawer();
      });
      this._updateChoiceProductPlusButton(pid);
      this._bindChoiceCardActions(materialItem, pid);
    }

    // Add collapse toggle functionality
    const collapseHeaders = materialItem.querySelectorAll('.collapse-header');
    collapseHeaders.forEach(header => {
      header.addEventListener('click', () =>
        this.toggleFormatCollapse(header)
      );
    });

    // Apply Smart Pick labels to all open collapses on page load
    const openCollapses = materialItem.querySelectorAll('.format-collapse.active');
    openCollapses.forEach(collapse => {
      //this.applySmartPickLabels(collapse);
    });

    const formatBtns = materialItem.querySelectorAll('.format-btn');
    formatBtns.forEach(btn => {
      btn.addEventListener('click', () =>
        this.handleFormatSelection(btn, materialItem, product)
      );
    });

    if (formatBtns.length === 1) {
      this._handleSingleFormat(materialItem, formatBtns[0], product);
    }

    if (formatBtns.length === 0) {
      this._checkSimpleProductNotifyMe(materialItem, product);
    }

    const purchaseCheckboxes =
      materialItem.querySelectorAll('.purchase-checkbox');
    purchaseCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () =>
        this.handlePurchaseSelection(checkbox)
      );
    });

    this._initMultiFormatWarning(materialItem);
    this._updateInBagIndicators(materialItem);
    cmrpSyncPurchaseTypeSelectedClass(materialItem);

    // Show used-materials notice on load (not only after format/variant interaction)
    materialItem.querySelectorAll('.collapse-content').forEach(collapse => {
      syncCmrpVariantNoticeInCollapse(collapse);
    });
  }

  /**
   * Toggle format collapse visibility
   * @param {HTMLElement} header - The collapse header element
   */
  toggleFormatCollapse(button) {
    const container = button.closest('.purchase-options-container');
    if (!container) return;

    // Stacked print+digital panels are both visible; the header is not a tab.
    if (container.hasAttribute('data-format-panel')) return;

    const format = button.dataset.format;
    const isActive = button.classList.contains('active');
    
    // If clicking on an already active tab, do nothing
    if (isActive) return;
    
    // Update tab states
    const allTabs = container.querySelectorAll('.format-tab');
    allTabs.forEach(tab => {
      tab.classList.remove('active');
      tab.setAttribute('aria-expanded', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-expanded', 'true');
    
    // Update collapse states
    const allCollapses = container.querySelectorAll('.format-collapse');
    allCollapses.forEach(c => {
      c.classList.remove('active');
    });
    
    // Show the selected format collapse
    const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
    if (targetCollapse) {
      targetCollapse.classList.add('active');
      //this.applySmartPickLabels(targetCollapse);
      const targetContent = targetCollapse.querySelector('.collapse-content');
      if (targetContent) {
        syncCmrpVariantNoticeInCollapse(targetContent);
      }
    } else {
      console.error('targetCollapse not found for format:', format);
    }
  }

  /**
   * Apply Smart Pick labels to the lowest price available option in each format
   * @param {HTMLElement} collapse - The format collapse element
   */
  applySmartPickLabels(collapse) {
    const purchaseTypes = collapse.querySelectorAll('.purchase-type:not(.unavailable)');
    if (purchaseTypes.length === 0) return;

    // Remove existing smart pick labels and classes
    purchaseTypes.forEach(type => {
      const existingLabel = type.querySelector('.smart-pick-label');
      if (existingLabel) {
        existingLabel.remove();
      }
      type.classList.remove('smart-pick');
    });

    // Find the lowest price option
    let lowestPrice = Infinity;
    let lowestPriceOption = null;

    purchaseTypes.forEach(type => {
      // Try multiple price selectors
      const priceElement = type.querySelector('.price') || 
                          type.querySelector('.purchase-price') ||
                          type.querySelector('[class*="price"]');
      
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
        const price = parseFloat(priceText);
        
        if (!isNaN(price) && price < lowestPrice) {
          lowestPrice = price;
          lowestPriceOption = type;
        }
      }
    });


    // Add Smart Pick label to the lowest price option
    if (lowestPriceOption) {
      const smartPickLabel = document.createElement('div');
      smartPickLabel.className = 'smart-pick-label';
      smartPickLabel.textContent = 'Smart Pick';
      
      // Make the purchase type container relative positioned and add smart-pick class
      lowestPriceOption.style.position = 'relative';
      lowestPriceOption.classList.add('smart-pick');
      lowestPriceOption.appendChild(smartPickLabel);
    }
  }

  _handleSingleFormat(materialItem, formatBtn, product) {
    formatBtn.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const format = formatBtn.getAttribute('data-format');
    if (format) this.updatePricingForFormat(materialItem, format, product);
  }

  toggleViewMore(button) {
    const materialItem = button.closest('.material-item');
    if (!materialItem) return;

    let detailsSection = materialItem.querySelector(
      '.material-details-expanded'
    );
    if (!detailsSection) {
      detailsSection = this._createDetailsSection(materialItem);
    }

    const isExpanded = button.classList.contains('expanded');
    this._toggleExpandedState(button, detailsSection, isExpanded, materialItem);
  }
  toggleViewMoreChoice(button,type) {
    const materialItem = button.closest('.material-item');
    if (!materialItem) return;

    let detailsSection = materialItem.querySelector(
      '.material-details-expanded'
    );
    if (!detailsSection) {
      detailsSection = this._createDetailsSection(materialItem);
    }

    const isExpanded = button.classList.contains('expanded');
    if (type == 'btn') {
      this._toggleExpandedStateChoice(
        button,
        detailsSection,
        isExpanded,
        materialItem
      );
    }
    if (type == 'checkbox') {
      this._toggleExpandedStateChoiceCheck(
        button,
        detailsSection,
        isExpanded,
        materialItem
      );
    }

    const pid = this._choiceProductIdFromToggleButton(button);
    if (pid) {
      requestAnimationFrame(() => {
        if (type === 'checkbox') {
          this._reconcileChoiceRowCheckedFromExpanded(pid);
        }
        this._updateChoiceProductPlusButton(pid);
      });
    }
  }

  _createDetailsSection(materialItem) {
    const detailsSection = document.createElement('div');
    detailsSection.className = 'material-details-expanded';
    detailsSection.id = `material-more-details-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    detailsSection.setAttribute('role', 'region');
    detailsSection.setAttribute('aria-label', 'Additional details');
    detailsSection.setAttribute('aria-hidden', 'true');
    detailsSection.setAttribute('aria-live', 'polite');
    detailsSection.tabIndex = -1;

    const targets = ['.material-details', '.material-text'];
    const parent =
      targets.map(sel => materialItem.querySelector(sel)).find(el => el) ||
      materialItem;
    parent.appendChild(detailsSection);

    return detailsSection;
  }

  _syncDetailsDisclosure(button, detailsSection, isOpen) {
    if (button) {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (detailsSection?.id) {
        button.setAttribute('aria-controls', detailsSection.id);
      }
    }
    if (!detailsSection) return;

    detailsSection.classList.toggle('expanded', isOpen);
    detailsSection.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    detailsSection.tabIndex = isOpen ? 0 : -1;

    const summary = String(detailsSection.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();
    detailsSection.setAttribute(
      'aria-label',
      summary ? `Additional details. ${summary}` : 'Additional details'
    );
  }

  _toggleExpandedState(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const text = isExpanded ? 'View more details' : 'View less details';
    const rotation = isExpanded ? '0deg' : '180deg';

    button.classList[action]('expanded');

    const spanElement = button.querySelector('span');
    if (spanElement) spanElement.textContent = text;

    const chevron = button.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    if (!isExpanded) {
      this.populateExpandedDetails(materialItem, detailsSection);
    }
    this._syncDetailsDisclosure(button, detailsSection, !isExpanded);
  }
  _toggleExpandedStateChoice(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const text = isExpanded ? 'View details' : 'Hide details';
    const rotation = isExpanded ? '0deg' : '180deg';
    const sectionScope = (button || materialItem)?.closest?.('.course-section') || document;

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const spanElement = button.querySelector('span');
    if (spanElement) spanElement.textContent = text;

    const chevron = button.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    const idProduct = this._choiceProductIdFromToggleButton(button);
    const choiceInfo = idProduct
      ? document.getElementById('choice-details-product-' + idProduct)
      : null;

    if (choiceInfo) {
      if (!isExpanded) {
        this._portalChoiceDrawerOpen(choiceInfo);
        choiceInfo.classList.add("choice-show");
        choiceInfo.classList.remove("choice-hide");
        if (!this._choiceDrawerUsesInlineLayout()) {
          cmrpChoiceTrapFocus(
            choiceInfo,
            button instanceof HTMLElement ? button : null
          );
        }
      } else {
        cmrpChoiceReleaseFocusTrap(true);
        choiceInfo.classList.add("choice-hide");
        choiceInfo.classList.remove("choice-show");
        this._portalChoiceDrawerClose(choiceInfo);
      }
    }
    this._syncChoiceDrawerScrollLock();
    if (idProduct) this._updateChoiceProductPlusButton(idProduct);
  }
  _toggleExpandedStateChoiceCheck(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const sectionScope = (button || materialItem)?.closest?.('.course-section') || document;

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const idProduct = this._choiceProductIdFromToggleButton(button);
    const choiceInfo = idProduct
      ? document.getElementById('choice-details-product-' + idProduct)
      : null;

    if (!isExpanded) {
      if (choiceInfo) {
        this._portalChoiceDrawerOpen(choiceInfo);
        choiceInfo.classList.add("choice-show");
        choiceInfo.classList.remove("choice-hide");
        if (!this._choiceDrawerUsesInlineLayout()) {
          cmrpChoiceTrapFocus(
            choiceInfo,
            button instanceof HTMLElement ? button : null
          );
        }
      }
    } else {
      if (choiceInfo) {
        cmrpChoiceReleaseFocusTrap(true);
        choiceInfo.classList.add("choice-hide");
        choiceInfo.classList.remove("choice-show");
        this._portalChoiceDrawerClose(choiceInfo);
      }

      if (idProduct) {
        this.updateViewMoreDetailsChoice(idProduct, sectionScope);

        if (!this._choiceDrawerUsesInlineLayout()) {
          const panel = document.getElementById(
            'choice-details-product-' + idProduct
          );
          const variantCheckboxes = panel
            ? panel.querySelectorAll('.purchase-checkbox')
            : sectionScope.querySelectorAll(
                `input[type="checkbox"][data-product-id="${idProduct}"]`
              );
          variantCheckboxes.forEach(cb => {
            if (cb.checked) {
              cb.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            }
          });

          this.hideShowPriceChoice(true, '', idProduct, sectionScope);
        } else {
          const panel = document.getElementById(
            'choice-details-product-' + idProduct
          );
          let selectedCount = 0;
          let priceProductSelected = '';
          panel
            ?.querySelectorAll('.purchase-checkbox:checked')
            .forEach(cb => {
              selectedCount += 1;
              const p = cb.getAttribute('data-variant-price');
              if (p) priceProductSelected = p;
            });
          const isAnySelected = selectedCount >= 2;
          this.hideShowPriceChoice(
            isAnySelected,
            priceProductSelected,
            idProduct,
            sectionScope
          );
          const rowCb = document.getElementById(
            'check-box-product-' + idProduct
          );
          const sid = rowCb?.getAttribute('data-section-id');
          if (sid) {
            this._syncChoiceMinimumAndExtraData(sid);
          }
        }

        const divDetails = sectionScope.querySelector(
          '#div-view-details-' + idProduct
        );
        if (divDetails) divDetails.classList.add('material-view-more-choice');
      }
    }
    this._syncChoiceDrawerScrollLock();
    if (idProduct) this._updateChoiceProductPlusButton(idProduct);
  }
  decodeHtmlEntities(str) {
      const txt = document.createElement("textarea"); 
      txt.innerHTML = str; 
      return txt.value; 
  }
  populateExpandedDetails(materialItem, detailsSection) {
    const titleElement = materialItem.querySelector('.material-title');
    if (!titleElement) {
      detailsSection.innerHTML = this._generateErrorHTML('No title found');
      return;
    }

    const product = window.courseData.products_Materials.find(
      p => this.decodeHtmlEntities(p.title) === titleElement.textContent.trim()
    );
    if (!product) {
      detailsSection.innerHTML = this._generateErrorHTML('Product not found');
      return;
    }

    const details = this._extractProductDetails(product);
    detailsSection.innerHTML = this._generateDetailsHTML(details);
  }

  _generateErrorHTML(message) {
    return `<div class="expanded-details-content"><div class="detail-row">Error: <span class="detail-value">${message}</span></div></div>`;
  }

  _extractProductDetails(product) {
    return {
      'Publisher':
        this.productHandler?.extractPublisher?.(product) || 'Not specified',
      'Format':
        this.productHandler?.extractFormat?.(product) || 'Not specified',
      'Copyright':
        this.productHandler?.extractCopyRight?.(product) || 'Not specified',
      'Publication':
        this.productHandler?.extractPublication?.(product) || 'Not specified'
    };
  }

  _generateDetailsHTML(details) {
    const rows = Object.entries(details)
      .map(
        ([key, value]) =>
          value === "Not specified" 
            ? ''  // Retorna uma string vazia se o valor for "Not Specficated"
            : `<div class="detail-row">${key}: <span class="detail-value">${value}</span></div>`
      )
      .join('');
    return `<div class="expanded-details-content">${rows}</div>`;
  }

  getVariantByOptions(product, format, type, condition = 'New') {
    if (!product.variants?.length) return null;

    return product.variants.find(variant => {
      const formatMatch = variant.option1
        ?.toLowerCase()
        .includes(format.toLowerCase());
      const typeMatch = this._matchPurchaseType(variant, type);
      const conditionMatch = this._matchCondition(variant, condition);
      return formatMatch && typeMatch && conditionMatch;
    });
  }

  _matchPurchaseType(variant, type) {
    if (type === 'buy') {
      return variant.option2?.toLowerCase().includes('buy');
    }
    if (type === 'rent' || type.startsWith('rent-')) {
      let typeMatch = variant.option2?.toLowerCase().includes('rent');
      if (type.includes('-')) {
        const requestedDays = type.split('-')[1];
        typeMatch = typeMatch && variant.option2?.includes(requestedDays);
      }
      return typeMatch;
    }
    return false;
  }

  _matchCondition(variant, condition) {
    if (!condition || condition === 'New') return true;
    return (
      variant.option1?.toLowerCase().includes(condition.toLowerCase()) ||
      variant.option3?.toLowerCase().includes(condition.toLowerCase())
    );
  }

  findVariantAcrossProducts(format, type, currentProduct, condition = 'New') {
    for (const product of this.products) {
      const variant = this.getVariantByOptions(
        product,
        format,
        type,
        condition
      );
      if (variant) return variant;
    }
    return null;
  }

  getSelectedFormat(materialItem) {
    if (!materialItem) return null;

    const manager = window.courseMaterialsPage?.components?.selectionManager;
    if (typeof manager?.getSelectedFormat === 'function') {
      return manager.getSelectedFormat(materialItem);
    }

    const checked = materialItem.querySelector('.purchase-checkbox:checked');
    if (checked?.dataset?.format) {
      return String(checked.dataset.format).toLowerCase();
    }

    const selectedBtn = materialItem.querySelector('.format-btn.selected');
    if (selectedBtn) {
      const fmt = selectedBtn.getAttribute('data-format');
      if (fmt) return String(fmt).toLowerCase();
    }

    if (materialItem.querySelector('[data-format-panel]')) {
      return null;
    }

    const activeTab = materialItem.querySelector(
      '.format-tab.active, .format-collapse.active'
    );
    if (activeTab) {
      const fmt = activeTab.getAttribute('data-format');
      if (fmt) return String(fmt).toLowerCase();
    }

    return null;
  }

  getInteractionStats() {
    const selectors = {
      totalMaterials: '.material-item',
      formatsSelected: '.format-btn.selected',
      itemsSelected: '.purchase-checkbox:checked',
      detailsExpanded: '.material-details-expanded.expanded',
    };

    return Object.entries(selectors).reduce((stats, [key, selector]) => {
      stats[key] = document.querySelectorAll(selector).length;
      return stats;
    }, {});
  }

  dispatchInteractionEvent(type, data = {}) {
    const event = new CustomEvent('materialInteraction:changed', {
      detail: { type, data, stats: this.getInteractionStats() },
    });
    document.dispatchEvent(event);
  }

  initialize() {
    const materialItems = document.querySelectorAll('.material-item');
    materialItems.forEach(item => {
      const titleElement = item.querySelector('.material-title');
      if (titleElement) {
        const product = this.products.find(
          p => p.title === titleElement.textContent.trim()
        );
        if (product) this.addMaterialEventListeners(item, product);
      }
    });
  }

  _ensurePurchaseTypeMeta(purchaseType) {
    let meta = purchaseType.querySelector(':scope > .purchase-type-meta');
    if (meta) return meta;

    meta = document.createElement('div');
    meta.className = 'purchase-type-meta';

    const rentalDuration = purchaseType.querySelector(
      ':scope > .rental-duration'
    );
    if (rentalDuration) {
      rentalDuration.replaceWith(meta);
      meta.appendChild(rentalDuration);
      return meta;
    }

    meta.classList.add('purchase-type-meta--badge-only');
    // Append last so badges never sit in the radio column
    const fee = purchaseType.querySelector(':scope > .purchase-type-digital-fee');
    if (fee) {
      purchaseType.insertBefore(meta, fee);
    } else {
      purchaseType.appendChild(meta);
    }
    return meta;
  }

  _placeInBagBadge(purchaseType, badge) {
    // Always last in the card stack (after title/price/requirements/rental), never under the radio.
    this._ensurePurchaseTypeMeta(purchaseType).appendChild(badge);
  }

  _getOrCreateInBagBadge(purchaseType) {
    let badge = purchaseType.querySelector('.in-bag-indicator');
    if (!badge) {
      const wrap = document.createElement('div');
      wrap.innerHTML = (window.generateInBagIndicatorHTML?.() || '').trim();
      badge = wrap.firstElementChild;

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'in-bag-indicator cmrp-in-bag-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.style.display = 'none';
        badge.innerHTML = '<span class="in-bag-text">Item In Bag</span>';
      }
    } else {
      badge.classList.add('cmrp-in-bag-badge');
      const text = badge.querySelector('.in-bag-text');
      if (text) text.textContent = 'Item In Bag';
      badge.querySelectorAll('svg').forEach((svg) => svg.remove());
    }

    const isInMeta = badge.closest('.purchase-type-meta') != null;
    if (!badge.parentElement || !isInMeta) {
      this._placeInBagBadge(purchaseType, badge);
    }

    return badge;
  }

  _setPurchaseTypeInBag(purchaseType, inCart) {
    purchaseType.classList.toggle('in-cart', inCart);

    const smartPickBadge = purchaseType.querySelector('.cmrp-smart-pick-badge');
    if (smartPickBadge) {
      smartPickBadge.style.display = inCart ? 'none' : '';
    }

    if (!inCart) {
      const badge = purchaseType.querySelector('.in-bag-indicator');
      if (badge) {
        badge.style.display = 'none';
        badge.setAttribute('aria-hidden', 'true');
        badge.removeAttribute('tabindex');
        badge.removeAttribute('aria-label');
      }
      this._syncInBagOnVariant(purchaseType, false);
      return;
    }

    const badge = this._getOrCreateInBagBadge(purchaseType);
    badge.style.display = 'inline-flex';
    badge.removeAttribute('aria-hidden');
    badge.removeAttribute('tabindex');
    badge.removeAttribute('aria-label');
    const text = badge.querySelector('.in-bag-text');
    if (text) {
      text.textContent = 'Item In Bag';
      text.removeAttribute('aria-hidden');
    }
    this._syncInBagOnVariant(purchaseType, true);
  }

  _syncInBagOnVariant(purchaseType, present) {
    const input = purchaseType.querySelector(
      'input.purchase-checkbox, input.purchase-choice-checkbox'
    );
    const text = purchaseType.querySelector('.in-bag-indicator .in-bag-text');
    if (text && !text.id) {
      const vid =
        purchaseType.getAttribute('data-variant-id') ||
        input?.getAttribute('data-variant-id') ||
        Math.random().toString(36).slice(2, 10);
      text.id = `in-bag-label-${vid}`;
    }
    cmrpFeeDescribedBy(input, text?.id, Boolean(present && text));
  }

  async _updateInBagIndicators(materialItem) {
    const cartChecker = window.cartChecker;
    if (!cartChecker) return;

    if (
      materialItem.classList.contains('choice-info') ||
      materialItem.classList.contains('choice-list')
    ) {
      return;
    }

    // Outer choice wrapper → update the nested product card (badge lives on variants)
    if (
      materialItem.classList.contains('choice-item') &&
      !materialItem.classList.contains('choice-product-card')
    ) {
      const card = materialItem.querySelector(
        '.material-item.choice-product-card'
      );
      if (card) await this._updateInBagIndicators(card);
      return;
    }

    // Choice cards keep purchase options in a sibling drawer
    let optionsRoot = materialItem;
    if (materialItem.classList.contains('choice-product-card')) {
      const productId = materialItem
        .querySelector('[data-product-id]')
        ?.getAttribute('data-product-id');
      const drawer =
        productId &&
        document.getElementById(`choice-details-product-${productId}`);
      if (drawer) optionsRoot = drawer;
    }

    const purchaseTypes = optionsRoot.querySelectorAll('.purchase-type');
    const variantIds = Array.from(purchaseTypes)
      .map(pt => pt.getAttribute('data-variant-id'))
      .filter(Boolean);

    if (variantIds.length === 0) {
      materialItem.classList.remove('in-cart');
      return;
    }

    try {
      const inCartResults =
        await cartChecker.checkMultipleVariants(variantIds);
      let anyInCart = false;

      purchaseTypes.forEach(purchaseType => {
        const variantId = purchaseType.getAttribute('data-variant-id');
        const inCart = Boolean(variantId && inCartResults[variantId]);
        if (inCart) anyInCart = true;
        this._setPurchaseTypeInBag(purchaseType, inCart);
      });

      materialItem.classList.toggle('in-cart', anyInCart);
    } catch (error) {}
  }

  async updateAllInBagIndicators() {
    const materialItems = document.querySelectorAll('.material-item');
    for (const materialItem of materialItems) {
      await this._updateInBagIndicators(materialItem);
    }
  }

  destroy() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.removeEventListener(event, this.handleCartUpdate)
    );

    if (typeof window !== 'undefined') {
      delete window.courseMaterialsInteractionManager;
    }
  }
}

window.MaterialInteractionManager = MaterialInteractionManager;

window.togglePurchaseOption = function (purchaseTypeDiv, event) {
  window.courseMaterialsInteractionManager?.togglePurchaseOption(
    purchaseTypeDiv,
    event
  );
};

window.handlePurchaseSelection = function (checkbox) {
  window.courseMaterialsInteractionManager?.handlePurchaseSelection(checkbox);
};

window.addMaterialEventListeners = function (materialItem, product) {
  window.courseMaterialsInteractionManager?.addMaterialEventListeners(
    materialItem,
    product
  );
};

window.handleNotifyMe = function (variantId, productTitle, format) {
  if (typeof window.openNotifyMeDialog === 'function') {
    window.openNotifyMeDialog(variantId, productTitle, format);
  }
};

window.toggleFormatCollapse = function (button) {
  window.courseMaterialsInteractionManager?.toggleFormatCollapse(button);
};